import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { Check, ExternalLink, MapPin, Printer, RefreshCw, Send, Tag, X } from 'lucide-react'
import { API_BASE_URL, TOKEN_KEY, adminApi } from '../../services/api'
import WhatsAppPanel from './WhatsAppPanel'
import { openPrintLabels } from './PrintLabel'
import { openServiceGroupLabels, openServiceGroupBatches, openOrderLabel, groupServiceName, type ServiceGroup, type PrintOrderMeta } from './printDocs'
import OrderQRCode from './OrderQRCode'
import OrderGroupsSection from './OrderGroupsSection'
import OrderExceptionsSection from './OrderExceptionsSection'
import type { QrPayload } from './orderQr'
import ReorderModal from './ReorderModal'
import CreateOrderModal from './CreateOrderModal'
import AgentPickerDropdown from './AgentPickerDropdown'
import OrderProofsSection from './OrderProofsSection'
import FinalBillCard from './FinalBillCard'
import ReschedulePendingCard from './ReschedulePendingCard'
import { useColors } from '../../hooks/useColors'
import { useToast } from '../../contexts/ToastContext'
import { useCan } from '../../hooks/useCan'
import {
  CANCEL_ALLOWED_STATES,
  POST_PICKUP_NON_TERMINAL_STATES,
  NON_TERMINAL_STATES as SHARED_NON_TERMINAL_STATES,
  customerFacingLabel,
} from '../../lib/orderStatus'
import { GoogleMap, MarkerF } from '@react-google-maps/api'
import { useMapsReady } from '../../hooks/useMapsReady'
import MapUnavailable from '../MapUnavailable'
import { useSocket } from '../../hooks/useSocket'
import { formatServiceName, getServicePromise } from '../../utils/serviceMetadata'

export interface OrderItem {
  id: string
  service_item_id?: string
  name?: string
  service_name?: string
  quantity?: number
  item_count?: number | null
  unit?: string | null
  total_price?: number
}

// "3.5 kg" for weight-priced items, "× 3" for counted items.
const formatItemQty = (quantity?: number, unit?: string | null, itemCount?: number | null): string => {
  const q = Number(quantity ?? 1)
  if (String(unit || '').toLowerCase() === 'per_kg') {
    const kg = `${String(Math.round(q * 100) / 100)} kg`
    const n = Number(itemCount)
    if (Number.isInteger(n) && n > 0) {
      return `${kg} · ${n} ${n === 1 ? 'item' : 'items'}`
    }
    return kg
  }
  return `× ${q}`
}

export interface OrderDelivery {
  id: string
  type?: string
  status?: string
  created_at?: string
  started_at?: string
  completed_at?: string
  agent_id?: string | null
  agent_name?: string | null
  agent_phone?: string | null
  agent_is_online?: boolean | null
  // Agent's pickup/item-verification photo (signed). The real pickup proof is
  // stored on the delivery row, not on orders.pickup_photo_url.
  verification_photo_url?: string | null
}

export interface OrderCustomer {
  id?: string
  name?: string
  phone?: string
  email?: string | null
}

export interface OrderAddress {
  id?: string
  label?: string | null
  full_address?: string
  landmark?: string
  lat?: number
  lng?: number
}

export interface OrderPartner {
  name?: string
}

export interface AssignedAgent {
  id?: string | null
  name?: string | null
  phone?: string | null
  is_online?: boolean | null
}

export interface OrderDetail {
  id: string
  order_number: string
  display_id?: string
  invoice_number?: string | null
  status: string
  total: number
  payment_status?: string | null
  /** What the customer CHOSE at checkout (cod, online, wallet, ...). */
  payment_mode?: string | null
  payment_method?: string | null
  /**
   * How the money actually ARRIVED (cash | upi | online). Distinct from
   * `payment_mode`: a COD order settled by the admin over UPI keeps
   * payment_mode='cod' and records 'upi' here.
   */
  payment_collected_method?: string | null
  paid_at?: string | null
  subtotal?: number
  delivery_fee?: number
  discount?: number
  coupon_code?: string | null
  // Full coupon shape (type/value/cap) so the verify-weight card can re-apply it
  // live in the recompute preview.
  coupon?: {
    code: string
    discount_type: 'percent' | 'flat'
    discount_value: number
    max_discount: number | null
    min_order: number
  } | null
  reweigh_note?: string | null
  admin_notes?: string | null
  created_at?: string | null
  updated_at?: string | null
  pickup_slot?: string
  pickup_date?: string | null
  delivery_slot?: string | null
  delivery_date?: string | null
  customer_name?: string
  customer_phone?: string
  partner_id?: string | null
  partner_name?: string | null
  service_name?: string
  delivery_model?: 'standard' | 'express' | 'instant' | string | null
  promised_delivery_at?: string | null
  workload_score?: number | null
  workload_minutes?: number | null
  express_slot_key?: string | null
  delivery_promise_label?: string | null
  agent_id?: string | null
  agent_name?: string | null
  agent_is_online?: boolean | null
  assigned_agent?: AssignedAgent | null
  customer?: OrderCustomer
  address?: OrderAddress
  partner?: OrderPartner
  items?: OrderItem[]
  groups?: { id: string; code?: string | null; label: string; delivery_model?: string; item_count: number; status: string; items?: { name: string; quantity: number; item_count?: number | null; unit?: string | null }[] }[]
  group_count?: number
  items_ready?: number
  items_total?: number
  deliveries?: OrderDelivery[]
  cancellation_reason?: string | null
  special_instructions?: string | null
  bag_count?: number | null
  inspection_notes?: string | null
  inspection_tags?: string[] | null
  pickup_photo_url?: string | null
  signature_url?: string | null
  cod_photo_url?: string | null
  hub_drop_photo_url?: string | null
  hub_drop_notes?: string | null
  hub_pickup_at?: string | null
  hub_pickup_by_agent_id?: string | null
  hub_pickup_confirmation?: string | null
  hub_pickup_photo_urls?: string[] | null
  // Per-item pickup photos captured by the agent (signed photo_url).
  garment_tags?: Array<{ id?: string; tag?: string | null; photo_url?: string | null }> | null
  reschedule_count?: number
  reschedule_reason?: string | null
  reschedule_proposed_by?: string | null
  new_pickup_date?: string | null
  new_pickup_slot?: string | null
  new_delivery_date?: string | null
  new_delivery_slot?: string | null
  requested_at?: string | null
  final_bill_status?: string
  final_amount?: number
  estimated_amount?: number
  calculated_payable_amount?: number | null
  /** Backend-resolved amounts (see backend utils/order-amount). */
  calculated_amount?: number | null
  final_order_amount?: number | null
  final_amount_paid?: number | null
  manual_adjustment_amount?: number | null
  manual_adjustment_remark?: string | null
  manual_adjustment_updated_by?: string | null
  manual_adjustment_updated_at?: string | null
  weight_kg?: number | null
  garment_count?: number | null
  qr_token?: string | null
  qr_payload?: QrPayload | null
  whatsapp_invoice?: {
    id?: string
    status?: 'pending' | 'sending' | 'sent' | 'delivered' | 'read' | 'failed' | 'skipped' | string
    provider_message_id?: string | null
    attempts?: number | null
    error?: string | null
    sent_at?: string | null
    delivered_at?: string | null
    failed_at?: string | null
    next_attempt_at?: string | null
    updated_at?: string | null
  } | null
}

interface TimelineEvent {
  label: string
  time?: string
  state: 'completed' | 'current' | 'pending'
}

interface AuditEvent {
  id?: string | number
  event?: string
  event_name?: string
  actor?: {
    kind?: string
    id?: string | null
  }
  actor_kind?: string
  actor_id?: string
  request_id?: string
  created_at?: string
  occurred_at?: string
  from_state?: string | null
  to_state?: string | null
  payload_hash?: string | null
  has_proof?: boolean
  proof_kind?: string | null
  geofence_warn?: boolean
  actor_name?: string | null
  station?: string | null
  scan_location?: { lat: number; lng: number } | null
  payload?: Record<string, unknown>
  pickup_photo_url?: string
  signature_url?: string
  cod_photo_url?: string
  hub_drop_photo_url?: string
}

interface OrderDetailPanelProps {
  order: OrderDetail
  onClose: () => void
  onAssignAgent: (orderId: string, anchorEl?: HTMLElement) => void
  onReorderSuccess?: () => void
  /**
   * Fired whenever this panel changes something the list/dashboard totals
   * depend on (payment status, final amount, order status), with the refreshed
   * order. Lets the caller update its row and re-ask the backend for totals
   * instead of the two views drifting apart.
   */
  onOrderChanged?: (order: OrderDetail) => void
}

const getInitials = (name: string | undefined) => {
  if (!name) {
    return 'CU'
  }

  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

// Map settings
const MAP_CONTAINER_STYLE = { width: '100%', height: '140px', borderRadius: '8px' }
const MAP_OPTIONS = {
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: 'cooperative',
}

// Build a Google Maps "drop a pin at this location" URL - coordinates when we
// have them, otherwise the free-text address. Mirrors the agent app's deep-link
// redirect but targets the universal web URL so it works in a desktop browser
// (and as an escape hatch when the embedded map can't load).
function buildMapsUrl({
  lat,
  lng,
  address,
}: {
  lat?: number | string | null
  lng?: number | string | null
  address?: string | null
}): string | null {
  const hasCoords = lat != null && lng != null && lat !== '' && lng !== ''
  if (hasCoords) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
  }
  if (address && address.trim()) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`
  }
  return null
}

function OpenInMapsLink({ url, style }: { url: string; style?: CSSProperties }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="font-body"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '13px',
        fontWeight: 600,
        color: '#0F7B6C',
        textDecoration: 'none',
        ...style,
      }}
    >
      <MapPin size={14} />
      Open in Google Maps
      <ExternalLink size={13} />
    </a>
  )
}

function AddressMiniMap({ lat, lng }: { lat: number; lng: number }) {
  const { keyMissing, isLoaded, loadError } = useMapsReady()

  // Missing/rejected key → show the shared fallback instead of hanging forever
  // on "Loading map…" (and never touch window.google when the lib didn't load).
  if (keyMissing) return <MapUnavailable reason="keyMissing" height={MAP_CONTAINER_STYLE.height} />
  if (loadError) return <MapUnavailable reason="loadError" height={MAP_CONTAINER_STYLE.height} />
  if (!isLoaded) return <div style={{ ...MAP_CONTAINER_STYLE, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: '#999', fontSize: '12px' }}>Loading map...</span></div>

  const center = { lat, lng }

  return (
    <div style={{ marginTop: '12px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #eee' }}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={center}
        zoom={15}
        options={MAP_OPTIONS}
      >
        <MarkerF
          position={center}
          icon={{
            path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z',
            fillColor: '#D6B97B',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
            scale: 1.5,
            anchor: new window.google.maps.Point(12, 24),
          }}
        />
      </GoogleMap>
    </div>
  )
}

const toTitle = (value: string) =>
  value
    .replace(/_/g, ' ')
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const inspectionTagStyle = (tag: string) => {
  switch (tag) {
    case 'stain':
      return { background: '#FDE68A', color: '#1F4D3A' };
    case 'damage':
      return { background: '#DC2626', color: '#F8F5EF' };
    case 'delicate':
      return { background: '#F8F5EF', color: '#D6B97B', border: '1px solid #D6B97B' };
    case 'premium':
      return { background: '#1F4D3A', color: '#F8F5EF' };
    case 'wet':
      return { background: '#1D4ED8', color: '#F8F5EF' };
    case 'smell':
      return { background: '#7C3AED', color: '#F8F5EF' };
    default:
      return { background: '#E5E7EB', color: '#111827' };
  }
}

const getStatusStyle = (status: string) => {
  const value = status.toLowerCase()

  if (value === 'placed') {
    return {
      background: 'rgba(214,185,123,0.12)',
      color: 'var(--accent-text)',
      border: '1px solid var(--accent)',
    }
  }

  if (value === 'agent_assigned') {
    return {
      background: 'var(--color-info-bg)',
      color: 'var(--color-info)',
      border: '1px solid var(--color-info-border)',
    }
  }

  if (value === 'picked_up' || value === 'out_for_delivery') {
    return {
      background: 'var(--color-warning-bg)',
      color: 'var(--color-warning)',
      border: '1px solid var(--color-warning-border)',
    }
  }

  if (['at_facility', 'in_wash', 'in_iron', 'quality_check', 'ready'].includes(value)) {
    return {
      background: 'var(--color-purple-bg)',
      color: 'var(--color-purple)',
      border: '1px solid var(--color-purple-border)',
    }
  }

  if (value === 'delivered') {
    return {
      background: 'var(--color-success-bg)',
      color: 'var(--color-success)',
      border: '1px solid var(--color-success-border)',
    }
  }

  return {
    background: 'var(--color-error-bg)',
    color: 'var(--color-error)',
    border: '1px solid var(--color-error-border)',
  }
}

const buildTimeline = (order: OrderDetail): TimelineEvent[] => {
  const baseEvents: TimelineEvent[] = [
    {
      label: 'Order placed',
      time: order.deliveries?.[0]?.created_at,
      state: 'completed',
    },
  ]

  if (order.hub_pickup_at) {
    const agentNote = order.hub_pickup_by_agent_id ? ` by Agent ${order.hub_pickup_by_agent_id.substring(0, 8)}` : ''
    const confirmNote = order.hub_pickup_confirmation ? `. ${order.hub_pickup_confirmation}` : ''
    baseEvents.push({
      label: `Collected from Hub${agentNote}${confirmNote}`,
      time: order.hub_pickup_at,
      state: 'completed',
    })
  }

  const deliveryEvents = (order.deliveries || []).map((delivery) => ({
    label: `${toTitle(delivery.type || 'Delivery')} ${toTitle(delivery.status || 'pending')}`,
    time: delivery.created_at,
    state: delivery.status === 'completed' ? ('completed' as const) : ('pending' as const),
  }))

  const merged = [...baseEvents, ...deliveryEvents]
  const firstPendingIndex = merged.findIndex((event) => event.state === 'pending')

  if (firstPendingIndex >= 0) {
    merged[firstPendingIndex] = {
      ...merged[firstPendingIndex],
      state: 'current',
    }
  }

  return merged
}

const formatPrice = (value: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(value || 0))

const roundMoney = (value: number) => Number((Number.isFinite(value) ? value : 0).toFixed(2))

const getCalculatedPayable = (order: Pick<OrderDetail, 'calculated_payable_amount' | 'final_amount' | 'final_bill_status' | 'total'>) => {
  if (order.calculated_payable_amount != null) return roundMoney(Number(order.calculated_payable_amount))
  if (
    order.final_amount != null &&
    ['approved', 'not_required'].includes(String(order.final_bill_status || 'not_required'))
  ) {
    return roundMoney(Number(order.final_amount))
  }
  return roundMoney(Number(order.total || 0))
}

const STATUS_STEPS = [
  { key: 'placed', label: 'Placed' },
  { key: 'agent_assigned', label: 'Assigned' },
  { key: 'picked_up', label: 'Picked Up' },
  { key: 'at_facility', label: 'At Facility' },
  { key: 'in_wash', label: 'In Wash' },
  { key: 'in_iron', label: 'In Iron' },
  { key: 'quality_check', label: 'Quality Check' },
  { key: 'ready', label: 'Ready' },
  { key: 'out_for_delivery', label: 'Delivery' },
  { key: 'delivered', label: 'Delivered' },
]

// Map every canonical backend status onto the nearest visible pipeline step.
// Without this the intermediate pickup/delivery sub-states (confirmed,
// searching_agent, en_route_pickup, arrived_pickup, arrived_dropoff) are not in
// STATUS_STEPS, so findIndex returns -1 and the pipeline wrongly falls back to
// "Placed" once the agent is en route / arrived.
const STATUS_TO_STEP_KEY: Record<string, string> = {
  placed: 'placed',
  confirmed: 'placed',
  searching_agent: 'placed',
  agent_assigned: 'agent_assigned',
  en_route_pickup: 'agent_assigned',
  arrived_pickup: 'agent_assigned',
  picked_up: 'picked_up',
  at_facility: 'at_facility',
  in_wash: 'in_wash',
  in_iron: 'in_iron',
  quality_check: 'quality_check',
  ready: 'ready',
  out_for_delivery: 'out_for_delivery',
  arrived_dropoff: 'out_for_delivery',
  delivered: 'delivered',
}

// Lifecycle state sets now come from the shared canonical module so every
// surface gates on the same definitions (Phase 2). NON_TERMINAL_STATES here
// keeps the historical behaviour of treating `delivered` as still actionable
// for force-fail, so we extend the shared set with it.
const NON_TERMINAL_STATES = new Set<string>([...SHARED_NON_TERMINAL_STATES, 'delivered'])

const formatTimestamp = (value?: string | null) => {
  if (!value) {
    return ''
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }

  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  })
}


const formatLastSeen = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return 'unknown'
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  return `${Math.floor(min / 60)}h ago`
}

const isPresignedUrlExpired = (url?: string | null) => {
  if (!url) return false
  try {
    const parsed = new URL(url)
    const expiresAt = parsed.searchParams.get('Expires')
    if (expiresAt && Number.isFinite(Number(expiresAt))) {
      return Date.now() >= Number(expiresAt) * 1000
    }

    const amzDate = parsed.searchParams.get('X-Amz-Date')
    const amzExpires = parsed.searchParams.get('X-Amz-Expires')
    if (amzDate && amzExpires) {
      const iso = amzDate.replace(
        /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
        '$1-$2-$3T$4:$5:$6Z',
      )
      const startedAt = new Date(iso).getTime()
      if (Number.isFinite(startedAt)) {
        return Date.now() >= startedAt + Number(amzExpires) * 1000
      }
    }
  } catch {
    return false
  }
  return false
}

const haversineKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371
  const toRad = (v: number) => (v * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export default function OrderDetailPanel({ order, onClose, onAssignAgent: _onAssignAgent, onReorderSuccess, onOrderChanged }: OrderDetailPanelProps) {
  void _onAssignAgent
  const can = useCan()
  const canUpdateOrders = can('orders.update')
  const canAssignOrders = can('orders.assign')
  const [orderDetail, setOrderDetail] = useState(order)
  const [notesDraft, setNotesDraft] = useState(order.admin_notes || '')
  const [actionError, setActionError] = useState('')
  const [isSavingNotes, setIsSavingNotes] = useState(false)
  const [weightDraft, setWeightDraft] = useState(order.weight_kg != null ? String(order.weight_kg) : '')
  const [isSavingWeight, setIsSavingWeight] = useState(false)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [updatingPayment, setUpdatingPayment] = useState(false)
  /** Open state of the Cash/UPI chooser behind Mark Paid. */
  const [markPaidMenuOpen, setMarkPaidMenuOpen] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [showQrModal, setShowQrModal] = useState(false)
  // Service-wise QR label printing: when set, the picker modal lists each
  // service group with its own editable quantity + Print button.
  const [servicePrintGroups, setServicePrintGroups] = useState<ServiceGroup[] | null>(null)
  const [serviceQtys, setServiceQtys] = useState<Record<string, string>>({})
  const [serviceSelected, setServiceSelected] = useState<Record<string, boolean>>({})
  const [cancelling, setCancelling] = useState(false)
  const [showReturnedDialog, setShowReturnedDialog] = useState(false)
  const [returnedReason, setReturnedReason] = useState('')
  const [markingReturned, setMarkingReturned] = useState(false)
  const [showForceFailDialog, setShowForceFailDialog] = useState(false)
  const [forceFailReason, setForceFailReason] = useState('')
  const [forceFailing, setForceFailing] = useState(false)
  const [showReorderModal, setShowReorderModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [latestAgentPosition, setLatestAgentPosition] = useState<{ lat: number; lng: number; recordedAt: string } | null>(null)
  const [partners, setPartners] = useState<Array<{ id: string; name: string }>>([])
  const [assigningPartner, setAssigningPartner] = useState(false)
  const [showAgentPicker, setShowAgentPicker] = useState(false)
  const signatureRefreshAttempts = useRef<Set<string>>(new Set())
  const [markingDelivered, setMarkingDelivered] = useState(false)
  const [finalAmountPaidDraft, setFinalAmountPaidDraft] = useState('')
  const [adjustmentRemarkDraft, setAdjustmentRemarkDraft] = useState('')
  const [savingPaymentSummary, setSavingPaymentSummary] = useState(false)
  const [isResendingInvoiceWhatsApp, setIsResendingInvoiceWhatsApp] = useState(false)
  const colors = useColors()
  const { addToast } = useToast()

  // Partners are fetched lazily, on first focus of the partner picker. Loading
  // them on mount cost a third request on EVERY order open for a dropdown most
  // opens never touch.
  const partnersRequested = useRef(false)
  const loadPartners = useCallback(() => {
    if (partnersRequested.current) return
    partnersRequested.current = true
    adminApi
      .getPartners()
      .then((res: unknown) => {
        const envelope = res as { data?: unknown }
        const raw = envelope?.data
        const data = Array.isArray(raw) ? raw : []
        setPartners(
          data
            .filter((p: { is_active?: boolean }) => p.is_active !== false)
            .map((p: { id: string; name?: string; user_name?: string }) => ({
              id: String(p.id),
              name: String(p.name || p.user_name || 'Partner'),
            })),
        )
      })
      .catch(() => { partnersRequested.current = false })
  }, [])

  // True while the authoritative detail is still in flight on open. The panel is
  // already visible and interactive; only the sections that need the extra data
  // show a placeholder.
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([])
  const [isLoadingAudit, setIsLoadingAudit] = useState(false)
  const [isActivityExpanded, setIsActivityExpanded] = useState(false)
  const [expandedActivityDetails, setExpandedActivityDetails] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setOrderDetail(order)
    setNotesDraft(order.admin_notes || '')
    setWeightDraft(order.weight_kg != null ? String(order.weight_kg) : '')
    setActionError('')
    setExpandedActivityDetails({})

    // The `order` prop comes from the orders list, whose row can be stale - e.g.
    // the agent advanced it to `at_facility` after the list loaded. The panel
    // subscribes to socket events, but only catches changes that arrive WHILE it
    // is open; a transition that happened before opening would leave the status
    // pipeline stuck on the stale list status. Fetch the authoritative detail on
    // open so the pipeline always reflects current server state. We update only
    // orderDetail (not notesDraft) to avoid clobbering an in-progress notes edit.
    // The panel opens instantly on the list row and fills in from here, so the
    // secondary sections (items, proofs, activity) show a light placeholder for
    // the one round trip rather than the whole screen freezing.
    let cancelled = false
    setIsLoadingDetail(!order.items)
    Promise.resolve(adminApi.getOrderDetail(order.id))
      .then(res => {
        if (cancelled) return
        const payload = (res as { data?: OrderDetail })?.data ?? (res as OrderDetail)
        if (payload && payload.id) setOrderDetail(payload)
      })
      .catch(() => { /* keep the list-row data as a fallback */ })
      .finally(() => { if (!cancelled) setIsLoadingDetail(false) })

    setIsLoadingAudit(true)
    adminApi.getOrderAuditTrail(order.id)
      .then(res => {
        setAuditEvents(res.data || [])
      })
      .catch(err => {
        console.error('Failed to fetch audit log:', err)
      })
      .finally(() => {
        setIsLoadingAudit(false)
      })

    return () => { cancelled = true }
  }, [order])

  useEffect(() => {
    const payable = getCalculatedPayable(orderDetail)
    setFinalAmountPaidDraft(String(orderDetail.final_amount_paid ?? payable))
    setAdjustmentRemarkDraft(orderDetail.manual_adjustment_remark || '')
  }, [
    orderDetail.id,
    orderDetail.total,
    orderDetail.final_amount,
    orderDetail.final_bill_status,
    orderDetail.calculated_payable_amount,
    orderDetail.final_amount_paid,
    orderDetail.manual_adjustment_remark,
  ])

  useEffect(() => {
    const handleClosePanel = () => onClose()
    document.addEventListener('close-panel', handleClosePanel)
    return () => document.removeEventListener('close-panel', handleClosePanel)
  }, [onClose])

  const statusStyle = getStatusStyle(orderDetail.status)
  const timelineEvents = buildTimeline(orderDetail)
  const items = orderDetail.items || []

  const paymentStatusValue = orderDetail.payment_status
  // Orders that never ran owe nothing, so they must not offer a collection.
  const NON_BILLABLE_STATUSES = ['cancelled', 'refunded', 'failed', 'no_show', 'returned']
  const canMarkPaid =
    paymentStatusValue === 'pending' &&
    !NON_BILLABLE_STATUSES.includes(String(orderDetail.status || '').toLowerCase())
  /**
   * How the money actually arrived. Falls back to the checkout choice ONLY for
   * an online/wallet settlement, where the gateway is unambiguously the source;
   * a COD order with no recorded method reads as unknown rather than being
   * guessed as cash.
   */
  const collectedMethodRaw = String(
    orderDetail.payment_collected_method ||
      (['online', 'wallet', 'card', 'netbanking'].includes(String(orderDetail.payment_mode || '').toLowerCase())
        ? orderDetail.payment_mode
        : '') ||
      ''
  ).toLowerCase()
  const collectedMethodLabel =
    collectedMethodRaw === 'upi'
      ? 'UPI'
      : collectedMethodRaw === 'cash'
        ? 'Cash'
        : collectedMethodRaw === 'wallet'
          ? 'Wallet'
          : collectedMethodRaw
            ? 'Online'
            : 'Not recorded'
  const paymentModeIsCod = String(orderDetail.payment_mode || '').toLowerCase() === 'cod'
  let paymentBadgeLabel = 'Unknown'
  let paymentBadgeBackground = 'var(--color-warning-bg)'
  let paymentBadgeColor = 'var(--color-warning)'
  let paymentBadgeBorder = 'var(--color-warning-border)'
  if (paymentStatusValue === 'paid') {
    paymentBadgeLabel = 'Paid'
    paymentBadgeBackground = 'var(--color-success-bg)'
    paymentBadgeColor = 'var(--color-success)'
    paymentBadgeBorder = 'var(--color-success-border)'
  } else if (paymentStatusValue === 'pending' && paymentModeIsCod) {
    paymentBadgeLabel = 'COD Pending'
    paymentBadgeBackground = '#fff7ed'
    paymentBadgeColor = '#c2410c'
    paymentBadgeBorder = '#fdba74'
  } else if (paymentStatusValue === 'pending') {
    paymentBadgeLabel = 'Pending'
    paymentBadgeBackground = 'var(--color-warning-bg)'
    paymentBadgeColor = 'var(--color-warning)'
    paymentBadgeBorder = 'var(--color-warning-border)'
  } else if (paymentStatusValue === 'failed') {
    paymentBadgeLabel = 'Failed'
    paymentBadgeBackground = 'var(--color-error-bg)'
    paymentBadgeColor = 'var(--color-error)'
    paymentBadgeBorder = 'var(--color-error-border)'
  } else if (paymentStatusValue === 'refunded') {
    paymentBadgeLabel = 'Refunded'
    paymentBadgeBackground = 'var(--color-error-bg)'
    paymentBadgeColor = 'var(--color-error)'
    paymentBadgeBorder = 'var(--color-error-border)'
  } else if (paymentStatusValue) {
    paymentBadgeLabel = paymentStatusValue
  }

  const currentStepKey = STATUS_TO_STEP_KEY[orderDetail.status] ?? orderDetail.status
  const currentIndex = STATUS_STEPS.findIndex((step) => step.key === currentStepKey)
  const safeIndex = currentIndex >= 0 ? currentIndex : 0
  const isCancelled = orderDetail.status === 'cancelled'
  // Lifecycle availability (state allows it) AND RBAC (role may mutate orders).
  // Mirrors the backend `orders.update` / `orders.assign` route guards.
  const canCancelLifecycle = canUpdateOrders && CANCEL_ALLOWED_STATES.has(orderDetail.status)
  const canMarkReturned = canUpdateOrders && POST_PICKUP_NON_TERMINAL_STATES.has(orderDetail.status)
  const canForceFail = canUpdateOrders && NON_TERMINAL_STATES.has(orderDetail.status)
  // Owner override: mark delivered directly from any non-terminal, non-delivered state.
  const canMarkDelivered = canUpdateOrders && NON_TERMINAL_STATES.has(orderDetail.status)
  const nextStep = safeIndex < STATUS_STEPS.length - 1 ? STATUS_STEPS[safeIndex + 1] : null
  const calculatedPayableAmount = getCalculatedPayable(orderDetail)
  const finalAmountPaidNumber = Number(finalAmountPaidDraft)
  const finalAmountPaidIsValid = finalAmountPaidDraft.trim() !== '' && Number.isFinite(finalAmountPaidNumber) && finalAmountPaidNumber >= 0
  // Declared here, after the amount it depends on: Mark Paid submits that
  // amount, so it cannot be enabled before the amount is known to be valid.
  const markPaidDisabled = updatingPayment || savingPaymentSummary || !finalAmountPaidIsValid
  const manualAdjustmentPreview = finalAmountPaidIsValid
    ? roundMoney(finalAmountPaidNumber - calculatedPayableAmount)
    : 0

  const pickupDelivery = orderDetail.deliveries?.find((delivery) => delivery.type === 'pickup')
  const dropDelivery = orderDetail.deliveries?.find((delivery) => delivery.type === 'drop')
  const assignedDelivery =
    orderDetail.deliveries?.find((delivery) => delivery.agent_id && delivery.status !== 'completed') ||
    orderDetail.deliveries?.find((delivery) => delivery.agent_id)
  const assignedAgent = orderDetail.assigned_agent || (assignedDelivery
    ? {
        id: assignedDelivery.agent_id,
        name: assignedDelivery.agent_name,
        phone: assignedDelivery.agent_phone,
        is_online: assignedDelivery.agent_is_online,
      }
    : null)
  const assignedAgentName = assignedAgent?.name || orderDetail.agent_name || null
  const assignedAgentOnline = assignedAgent?.is_online ?? orderDetail.agent_is_online ?? false
  const agentId = assignedAgent?.id || pickupDelivery?.agent_id || dropDelivery?.agent_id
  const hasPickupAgent = Boolean(pickupDelivery?.agent_id || assignedAgentName)
  const hasDeliveryAgent = Boolean(dropDelivery?.agent_id)
  const needsPickupAgent = canAssignOrders && orderDetail.status === 'placed' && !hasPickupAgent
  const needsDeliveryAgent =
    canAssignOrders &&
    (orderDetail.status === 'out_for_delivery' || orderDetail.status === 'ready') &&
    !hasDeliveryAgent
  const canAdvanceStatus = !isCancelled && nextStep && !(orderDetail.status === 'placed' && nextStep.key === 'agent_assigned')

  const statusTimestamps: Record<string, string | null | undefined> = {
    placed: orderDetail.created_at,
    agent_assigned: pickupDelivery?.created_at,
    picked_up: pickupDelivery?.completed_at || pickupDelivery?.started_at,
    at_facility: null,
    in_wash: null,
    in_iron: null,
    quality_check: null,
    ready: null,
    out_for_delivery: dropDelivery?.started_at || dropDelivery?.created_at,
    delivered: dropDelivery?.completed_at,
  }

  const getStepTime = (stepKey: string, index: number) => {
    if (isCancelled) {
      return ''
    }

    const explicitTime = statusTimestamps[stepKey]

    if (index <= safeIndex) {
      if (index === safeIndex) {
        return formatTimestamp(orderDetail.updated_at || explicitTime)
      }
      return formatTimestamp(explicitTime)
    }

    return ''
  }

  const pickupLat = Number(orderDetail.address?.lat)
  const pickupLng = Number(orderDetail.address?.lng)
  const hasPickupCoords = Number.isFinite(pickupLat) && Number.isFinite(pickupLng)
  const distanceFromPickupKm = latestAgentPosition && hasPickupCoords
    ? haversineKm(pickupLat, pickupLng, latestAgentPosition.lat, latestAgentPosition.lng)
    : null
  const whatsappInvoice = orderDetail.whatsapp_invoice
  const whatsappInvoiceStatus = whatsappInvoice?.status || 'pending'
  const whatsappInvoiceStatusLabel = whatsappInvoiceStatus === 'read'
    ? 'Read'
    : whatsappInvoiceStatus === 'delivered'
      ? 'Delivered'
      : whatsappInvoiceStatus === 'sent'
        ? 'Sent'
        : whatsappInvoiceStatus === 'sending'
          ? 'Sending'
          : whatsappInvoiceStatus === 'failed'
            ? 'Failed'
            : whatsappInvoiceStatus === 'skipped'
              ? 'Skipped'
              : 'Pending'
  const whatsappInvoiceCanRetry = canUpdateOrders && ['failed', 'skipped'].includes(whatsappInvoiceStatus)
  // Provider/internal codes are engineer-facing. Admins need to know what to DO,
  // so map the codes we raise ourselves to plain English and fall back to the
  // raw provider text for anything MSG91 sends back that we don't recognise.
  const whatsappInvoiceReason = (() => {
    const raw = whatsappInvoice?.error
    if (!raw) return null
    if (raw.includes('INVALID_PHONE_NUMBER')) return 'Customer phone number missing or invalid'
    if (raw.includes('FEATURE_DISABLED')) return 'WhatsApp invoice automation is turned off'
    if (raw.includes('MSG91_INVOICE_CONFIG_MISSING')) return 'MSG91 is not fully configured'
    if (raw.includes('MSG91_INVOICE_TEMPLATE_NOT_FOUND')) return 'WhatsApp template not found in MSG91'
    if (raw.includes('MSG91_INVOICE_TEMPLATE_NOT_APPROVED')) return 'WhatsApp template not approved yet'
    if (raw.includes('MSG91_INVOICE_TEMPLATE_VARIABLE_MISMATCH')) return 'WhatsApp template variables do not match'
    if (raw.includes('MISSING_PUBLIC_API_BASE_URL')) return 'Public API URL not configured for invoice links'
    if (raw.includes('ORDER_CANCELLED')) return 'Order was cancelled. No invoice to send'
    // Meta-side failures. Retrying these changes nothing until the template
    // itself is fixed, so the copy has to say so rather than inviting a reflex
    // click on Retry.
    if (raw.includes('META_MARKETING_FREQUENCY_CAP')) {
      return 'Blocked by Meta. The invoice template is categorised as MARKETING, not UTILITY. '
        + 'Retrying will not help until the template is replaced with a non-promotional one.'
    }
    if (raw.includes('META_MARKETING_EXPERIMENT_GROUP')) return 'Blocked by Meta marketing limits. Template must be UTILITY'
    if (raw.includes('META_UNDELIVERABLE_RECIPIENT')) return 'This number cannot receive WhatsApp messages'
    if (raw.includes('META_MEDIA_DOWNLOAD_FAILED')) return 'Meta could not download the invoice PDF in time. Retry is worth a try'
    if (raw.includes('META_MEDIA_UPLOAD_FAILED')) return 'Meta rejected the invoice PDF (size or format)'
    if (raw.includes('META_TEMPLATE_PARAM_MISMATCH')) return 'Template variables do not match the approved template'
    if (raw.includes('META_TEMPLATE_NOT_FOUND')) return 'Template not found or not approved in Meta'
    return raw
  })()

  const refreshOrder = async () => {
    const response = await adminApi.getOrderDetail(orderDetail.id)
    const payload = response?.data ?? response
    setOrderDetail(payload)
    setNotesDraft(payload?.admin_notes || '')
    setWeightDraft(payload?.weight_kg != null ? String(payload.weight_kg) : '')
    // Whatever changed here (payment, final amount, status) also moves the
    // orders table and every summary total - tell the parent with the
    // authoritative row rather than letting it guess.
    if (payload?.id) onOrderChanged?.(payload)
  }

  useEffect(() => {
    const signatureUrl = orderDetail.signature_url
    if (!signatureUrl) return
    if (!isPresignedUrlExpired(signatureUrl)) return
    if (signatureRefreshAttempts.current.has(signatureUrl)) return

    signatureRefreshAttempts.current.add(signatureUrl)
    void (async () => {
      try {
        const response = await adminApi.getOrderDetail(orderDetail.id)
        const payload = response?.data ?? response
        setOrderDetail(payload)
        setNotesDraft(payload?.admin_notes || '')
        setWeightDraft(payload?.weight_kg != null ? String(payload.weight_kg) : '')
      } catch {
        // Non-blocking: keep stale signature URL and let manual refresh recover.
      }
    })()
  }, [orderDetail.id, orderDetail.signature_url])

  useSocket({
    'order:status_changed': (payload: Record<string, unknown>) => {
      if (payload?.order_id === orderDetail.id) {
        void refreshOrder()
      }
    },
    'order:agent_assigned': (payload: Record<string, unknown>) => {
      if (payload?.order_id === orderDetail.id) {
        void refreshOrder()
      }
    },
    'order:agent_unassigned': (payload: Record<string, unknown>) => {
      if (payload?.order_id === orderDetail.id) {
        void refreshOrder()
      }
    },
    'order:payment_updated': (payload: Record<string, unknown>) => {
      if (payload?.order_id === orderDetail.id) {
        void refreshOrder()
      }
    },
    'order:cash_collected': (payload: Record<string, unknown>) => {
      if (payload?.order_id === orderDetail.id) {
        void refreshOrder()
      }
    },
    'agent:location_updated': (payload: Record<string, unknown>) => {
      if (payload?.order_id === orderDetail.id && payload.lat != null && payload.lng != null) {
        setLatestAgentPosition({
          lat: Number(payload.lat),
          lng: Number(payload.lng),
          recordedAt: String(payload.recorded_at || new Date().toISOString()),
        })
      }
    },
  })

  /**
   * Settle the order, recording HOW the money arrived. The method is never
   * inferred: the admin picks Cash or UPI, the backend persists it alongside a
   * timestamp and an evidence row, and a repeat click is refused there (409)
   * rather than creating a second payment.
   */
  const handleMarkPaid = async (method: 'cash' | 'upi') => {
    if (!finalAmountPaidIsValid) {
      addToast('Enter a valid final amount paid', 'error')
      return
    }
    setMarkPaidMenuOpen(false)
    setUpdatingPayment(true)
    try {
      await adminApi.updatePaymentStatus(orderDetail.id, 'paid', {
        final_amount_paid: roundMoney(finalAmountPaidNumber),
        adjustment_remark: adjustmentRemarkDraft.trim() || undefined,
        payment_method: method,
      })
      await refreshOrder()
      addToast(`Payment recorded as ${method === 'upi' ? 'UPI' : 'cash'}`, 'success')
    } catch (err: unknown) {
      // The backend refuses a duplicate settlement with 409. Re-read the order
      // so the panel shows the payment that already exists instead of leaving
      // the admin looking at a stale "pending".
      const status =
        err && typeof err === 'object' && 'response' in err && err.response && typeof err.response === 'object' && 'status' in err.response
          ? Number((err.response as { status?: number }).status)
          : null
      if (status === 409) {
        await refreshOrder()
        addToast('This order is already marked paid.', 'error')
        setUpdatingPayment(false)
        return
      }
      const message =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'message' in err.response.data
          ? String((err.response.data as { message?: string }).message)
          : 'Failed to update payment'
      addToast(message, 'error')
    } finally {
      setUpdatingPayment(false)
    }
  }

  const handleResendInvoiceWhatsApp = async () => {
    setIsResendingInvoiceWhatsApp(true)
    try {
      await adminApi.resendInvoiceWhatsApp(orderDetail.id)
      await refreshOrder()
      addToast('Invoice WhatsApp retry queued', 'success')
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'message' in err.response.data
          ? String((err.response.data as { message?: string }).message)
          : 'Failed to retry invoice WhatsApp'
      addToast(message, 'error')
    } finally {
      setIsResendingInvoiceWhatsApp(false)
    }
  }

  const handleSavePaymentSummary = async () => {
    if (!finalAmountPaidIsValid) {
      addToast('Enter a valid final amount paid', 'error')
      return
    }
    setSavingPaymentSummary(true)
    try {
      await adminApi.updatePaymentStatus(orderDetail.id, orderDetail.payment_status || 'pending', {
        final_amount_paid: roundMoney(finalAmountPaidNumber),
        adjustment_remark: adjustmentRemarkDraft.trim() || undefined,
      })
      await refreshOrder()
      addToast('Payment summary updated', 'success')
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'message' in err.response.data
          ? String((err.response.data as { message?: string }).message)
          : 'Failed to update payment summary'
      addToast(message, 'error')
    } finally {
      setSavingPaymentSummary(false)
    }
  }


  const handleAdvanceStatus = async () => {
    if (!nextStep) {
      return
    }

    const confirmed = window.confirm(`Change status to ${toTitle(nextStep.key)}?`)
    if (!confirmed) {
      return
    }

    setIsUpdatingStatus(true)
    setActionError('')

    try {
      await adminApi.updateOrderStatus(orderDetail.id, nextStep.key)
      await refreshOrder()
      addToast('Order status updated successfully', 'success')
    } catch (err) {
      console.error(err)
      // Surface the real backend reason (invalid transition / validation) instead
      // of a generic string, so operators can see WHY a "Move to" was rejected.
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || 'Unable to update status. Please try again.'
      setActionError(msg)
      addToast(msg, 'error')
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  // Records what the scale said at the hub. Deliberately does NOT reprice the
  // order - see the /weight endpoint comment.
  const handleSaveWeight = async () => {
    const trimmed = weightDraft.trim()
    const parsed = trimmed === '' ? null : Number(trimmed)
    if (parsed !== null && (!Number.isFinite(parsed) || parsed <= 0)) {
      addToast('Enter a valid weight in kg', 'error')
      return
    }

    setIsSavingWeight(true)
    try {
      await adminApi.updateOrderWeight(orderDetail.id, parsed)
      await refreshOrder()
      addToast(parsed === null ? 'Processed weight cleared' : 'Processed weight saved', 'success')
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Unable to save the processed weight. Please try again.'
      addToast(message, 'error')
    } finally {
      setIsSavingWeight(false)
    }
  }

  const handleSaveNotes = async () => {
    setIsSavingNotes(true)
    setActionError('')

    try {
      await adminApi.updateOrderNotes(orderDetail.id, notesDraft.trim() || null)
      await refreshOrder()
      addToast('Notes updated successfully', 'success')
    } catch (err) {
      console.error(err)
      setActionError('Unable to save notes. Please try again.')
      addToast('Unable to save notes. Please try again.', 'error')
    } finally {
      setIsSavingNotes(false)
    }
  }

  const handleConfirmCancel = async () => {
    if (cancelReason.trim().length < 10) {
      addToast('Please provide a cancellation reason (at least 10 characters).', 'error')
      return
    }

    setCancelling(true)
    setActionError('')

    try {
      await adminApi.updateOrderStatus(orderDetail.id, 'cancelled', cancelReason.trim())
      await refreshOrder()
      setShowCancelDialog(false)
      setCancelReason('')
      addToast('Order cancelled successfully', 'success')
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'message' in err.response.data
          ? String((err.response.data as { message?: string }).message)
          : 'Failed to cancel'
      addToast(message, 'error')
    } finally {
      setCancelling(false)
    }
  }

  const handleMarkReturned = async () => {
    if (returnedReason.trim().length < 10) {
      addToast('Reason must be at least 10 characters.', 'error')
      return
    }
    setMarkingReturned(true)
    try {
      await adminApi.transitionOrderState(orderDetail.id, 'returned', { reason: returnedReason.trim() })
      await refreshOrder()
      setShowReturnedDialog(false)
      setReturnedReason('')
      addToast('Order marked returned', 'success')
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'message' in err.response.data
          ? String((err.response.data as { message?: string }).message)
          : 'Failed to mark returned'
      addToast(message, 'error')
    } finally {
      setMarkingReturned(false)
    }
  }

  const handleMarkDelivered = async () => {
    if (!window.confirm('Mark this order as delivered? This bypasses the delivery-OTP flow.')) return
    setMarkingDelivered(true)
    try {
      await adminApi.markOrderDelivered(orderDetail.id)
      await refreshOrder()
      addToast('Order marked delivered', 'success')
    } catch (err: unknown) {
      const message =
        (err && typeof err === 'object' && 'response' in err &&
          (err as { response?: { data?: { message?: string } } }).response?.data?.message) ||
        'Failed to mark delivered'
      addToast(String(message), 'error')
    } finally {
      setMarkingDelivered(false)
    }
  }

  const handleForceFail = async () => {
    if (forceFailReason.trim().length < 10) {
      addToast('Reason must be at least 10 characters.', 'error')
      return
    }
    setForceFailing(true)
    try {
      await adminApi.transitionOrderState(orderDetail.id, 'failed', { reason: forceFailReason.trim() })
      await refreshOrder()
      setShowForceFailDialog(false)
      setForceFailReason('')
      addToast('Order force-failed', 'success')
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'message' in err.response.data
          ? String((err.response.data as { message?: string }).message)
          : 'Failed to force fail order'
      addToast(message, 'error')
    } finally {
      setForceFailing(false)
    }
  }

  const buildPrintMeta = (): PrintOrderMeta => {
    const od = orderDetail as typeof orderDetail & {
      invoice_number?: string | null
      address?: { full_address?: string }
      payment?: { razorpay_payment_id?: string | null; created_at?: string | null } | null
    }
    const addr = od.address?.full_address || null
    return {
      invoiceNumber: od.invoice_number || null,
      orderNumber: orderDetail.order_number,
      displayId: orderDetail.display_id,
      customerName: orderDetail.customer?.name || orderDetail.customer_name || 'Customer',
      customerPhone: orderDetail.customer?.phone || orderDetail.customer_phone || undefined,
      customerEmail: orderDetail.customer?.email || null,
      customerId: orderDetail.customer?.id || null,
      pickupAddress: addr,
      deliveryAddress: addr,
      createdAt: orderDetail.created_at || null,
      pickupDate: orderDetail.pickup_date || null,
      pickupSlot: orderDetail.pickup_slot || null,
      subtotal: orderDetail.subtotal,
      deliveryFee: orderDetail.delivery_fee,
      discount: orderDetail.discount,
      total: orderDetail.total,
      calculatedPayableAmount: orderDetail.calculated_payable_amount ?? getCalculatedPayable(orderDetail),
      finalAmountPaid: orderDetail.final_amount_paid ?? null,
      manualAdjustmentAmount: orderDetail.manual_adjustment_amount ?? null,
      manualAdjustmentRemark: orderDetail.manual_adjustment_remark ?? null,
      paymentStatus: orderDetail.payment_status,
      paymentMode: orderDetail.payment_mode,
      transactionId: od.payment?.razorpay_payment_id || null,
      paidAt: orderDetail.payment_status === 'paid' ? (od.payment?.created_at || null) : null,
      qrPayload: orderDetail.qr_payload,
      qrToken: orderDetail.qr_token,
    }
  }

  // One order-level QR label (thermal 50x30) for the pickup bag.
  const handlePrintOrderLabel = () => {
    const totalItems = (orderDetail.items || []).reduce((n, it) => n + Number(it.quantity || 1), 0)
    // Ask how many labels to print (one per garment bag). Each page is the same
    // QR + order id, numbered i/N so staff can match them through the workflow.
    const suggested = Math.max(1, Math.round(totalItems) || 1)
    const input = window.prompt(
      'How many QR labels to print for this order?\nEach label is numbered (1/N, 2/N …).',
      String(suggested),
    )
    if (input === null) return // cancelled
    const copies = Math.max(1, Math.min(200, Math.floor(Number(input)) || 1))
    void openOrderLabel(buildPrintMeta(), totalItems, copies)
  }

  const fetchGroupLabels = async (): Promise<ServiceGroup[]> => {
    try {
      const res = (await adminApi.getOrderGroupLabels(orderDetail.id)) as { data?: { labels?: ServiceGroup[] } } | { labels?: ServiceGroup[] }
      const payload = (res as { data?: { labels?: ServiceGroup[] } }).data ?? (res as { labels?: ServiceGroup[] })
      return Array.isArray(payload?.labels) ? (payload.labels as ServiceGroup[]) : []
    } catch {
      return []
    }
  }

  // Suggested bag count for a service = its item count, rounded to a whole
  // number (weight-based groups arrive as e.g. 3.4), min 1. Editable in the picker.
  const suggestedBagCount = (itemCount: unknown): string =>
    String(Math.max(1, Math.round(Number(itemCount)) || 1))

  // Clamp a raw quantity string from the picker to a sane bag count.
  const clampCopies = (raw: unknown): number => Math.max(1, Math.min(200, Math.floor(Number(raw)) || 1))

  // Print `copies` bag labels for ONE service group (Bag 1/N … Bag N/N), each
  // showing that service's name + code. This is the per-service batch.
  const printServiceGroup = (g: ServiceGroup) => {
    openServiceGroupLabels(buildPrintMeta(), g, clampCopies(serviceQtys[g.group_id]))
  }

  // Print every SELECTED service in ONE print job - each service keeps its own
  // Bag i/N sequence. Saves the staff clicking Print once per service.
  const printSelectedServices = () => {
    if (!servicePrintGroups) return
    const batches = servicePrintGroups
      .filter((g) => serviceSelected[g.group_id])
      .map((g) => ({ group: g, copies: clampCopies(serviceQtys[g.group_id]) }))
    if (!batches.length) return
    openServiceGroupBatches(buildPrintMeta(), batches)
  }

  // Open the service-wise picker when the order has service groups (laundry ops
  // are per-service: different teams/stations, so each service prints its own
  // batch with its own quantity). Fall back to per-item labels for legacy/
  // ungrouped orders.
  const handlePrintLabels = async () => {
    const groups = await fetchGroupLabels()
    if (groups.length) {
      const qtys: Record<string, string> = {}
      const selected: Record<string, boolean> = {}
      for (const g of groups) {
        qtys[g.group_id] = suggestedBagCount(g.item_count)
        selected[g.group_id] = true // all services checked by default
      }
      setServiceQtys(qtys)
      setServiceSelected(selected)
      setServicePrintGroups(groups)
      return
    }
    const itemsForPrint = (orderDetail.items || []).map((item) => ({
      name: item.service_name || item.name || 'Item',
      quantity: Number(item.quantity || 1),
    }))
    void openPrintLabels([
      {
        orderNumber: orderDetail.order_number,
        customerName: orderDetail.customer?.name || orderDetail.customer_name || 'Customer',
        items: itemsForPrint,
        createdAt: orderDetail.created_at || null,
        qrPayload: orderDetail.qr_payload,
        qrToken: orderDetail.qr_token,
        orderId: orderDetail.id,
      },
    ], addToast)
  }

  // The invoice always comes from the render service - the same PDF the
  // customer downloads and the same one WhatsApp attaches. The dashboard no
  // longer builds its own; a locally-assembled copy is how admin print drifted
  // from the customer's invoice in the first place.
  const handleOpenInvoice = async () => {
    // New tab can't send the Authorization header - pass the JWT via ?token=
    // (backend allows it for GET downloads) so the invoice isn't "Unauthorized".
    const token = localStorage.getItem(TOKEN_KEY)
    window.open(
      `${API_BASE_URL}/admin/orders/${orderDetail.id}/invoice${
        token ? `?token=${encodeURIComponent(token)}` : ''
      }`,
      '_blank',
    )
  }

  return (
    <>
      {servicePrintGroups ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Print QR labels by service"
          onClick={() => setServicePrintGroups(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(0,0,0,0.55)', display: 'grid', placeItems: 'center', padding: '24px' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: 'min(420px, 92vw)', maxHeight: '80vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#0F2E2A' }}>Print QR Labels</h3>
              <button type="button" aria-label="Close" onClick={() => setServicePrintGroups(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#4A5568', padding: 4 }}>
                <X size={18} />
              </button>
            </div>
            <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#4A5568' }}>
              {orderDetail.order_number} · one batch per service. Each label is numbered Bag&nbsp;1/N.
            </p>
            {(() => {
              const selectedGroups = servicePrintGroups.filter((g) => serviceSelected[g.group_id])
              const totalLabels = selectedGroups.reduce((n, g) => n + clampCopies(serviceQtys[g.group_id]), 0)
              const allSelected = selectedGroups.length === servicePrintGroups.length
              return (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 4 }}>
                    <button
                      type="button"
                      onClick={() => setServiceSelected(Object.fromEntries(servicePrintGroups.map((g) => [g.group_id, !allSelected])))}
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#0F2E2A', fontSize: 12, fontWeight: 600, padding: 0, textDecoration: 'underline' }}
                    >
                      {allSelected ? 'Clear all' : 'Select all'}
                    </button>
                    <span style={{ fontSize: 12, color: '#777' }}>Qty = bags</span>
                  </div>
                  {servicePrintGroups.map((g) => {
                    const express = String(g.delivery_model).toLowerCase() === 'express' ? ' ⚡' : ''
                    const checked = !!serviceSelected[g.group_id]
                    return (
                      <div key={g.group_id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderTop: '1px solid #F2EEE3' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          aria-label={`Include ${groupServiceName(g)}`}
                          onChange={(e) => setServiceSelected((prev) => ({ ...prev, [g.group_id]: e.target.checked }))}
                          style={{ width: 17, height: 17, accentColor: '#0F2E2A', cursor: 'pointer', flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: '#0F2E2A', fontWeight: 700, fontSize: '13.5px' }}>
                            {g.code ? <span style={{ background: '#D6B97B', color: '#0F2E2A', borderRadius: 6, padding: '1px 6px', fontSize: 11, marginRight: 6 }}>{g.code}</span> : null}
                            {groupServiceName(g)}{express}
                          </div>
                          <div style={{ color: '#777', fontSize: 12, marginTop: 2 }}>
                            {Number(Number(g.item_count).toFixed(2))} item{Number(g.item_count) === 1 ? '' : 's'}
                          </div>
                        </div>
                        <input
                          type="number"
                          min={1}
                          max={200}
                          aria-label={`Number of labels for ${groupServiceName(g)}`}
                          value={serviceQtys[g.group_id] ?? ''}
                          onChange={(e) => setServiceQtys((prev) => ({ ...prev, [g.group_id]: e.target.value }))}
                          style={{ width: 56, height: 34, padding: '0 8px', borderRadius: 8, border: '1px solid #D8D2C4', fontSize: 14, textAlign: 'center' }}
                        />
                        <button
                          type="button"
                          title={`Print only ${groupServiceName(g)}`}
                          onClick={() => printServiceGroup(g)}
                          style={{ border: '1px solid #0F2E2A', background: '#fff', borderRadius: 8, padding: '0 12px', height: 34, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0F2E2A' }}
                        >
                          Print
                        </button>
                      </div>
                    )
                  })}
                  <div style={{ display: 'flex', gap: 10, marginTop: 14, borderTop: '1px solid #E8E2D4', paddingTop: 14 }}>
                    <button
                      type="button"
                      onClick={() => setServicePrintGroups(null)}
                      style={{ flex: '0 0 auto', border: '1px solid #D8D2C4', background: '#fff', borderRadius: 999, padding: '0 18px', height: 40, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#4A5568' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => { printSelectedServices(); setServicePrintGroups(null) }}
                      disabled={selectedGroups.length === 0}
                      style={{ flex: 1, border: 'none', borderRadius: 999, height: 40, cursor: selectedGroups.length === 0 ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700, background: selectedGroups.length === 0 ? '#9CA3AF' : '#0F2E2A', color: '#F3EFE6', opacity: selectedGroups.length === 0 ? 0.7 : 1 }}
                    >
                      {allSelected ? 'Print All Services' : 'Print Selected'}
                      {selectedGroups.length ? ` · ${totalLabels} label${totalLabels === 1 ? '' : 's'}` : ''}
                    </button>
                  </div>
                </>
              )
            })()}
          </div>
        </div>
      ) : null}
      {showQrModal ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setShowQrModal(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(0,0,0,0.55)', display: 'grid', placeItems: 'center', padding: '24px' }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', padding: '24px', textAlign: 'center', display: 'grid', placeItems: 'center', gap: '8px' }}>
            <OrderQRCode
              qrPayload={orderDetail.qr_payload}
              qrToken={orderDetail.qr_token}
              orderId={orderDetail.id}
              orderNumber={orderDetail.order_number}
              size={260}
            />
            <p style={{ margin: 0, fontFamily: '"Courier New", monospace', fontSize: '14px', fontWeight: 700, color: '#0F2E2A' }}>{orderDetail.order_number}</p>
            <p style={{ margin: 0, fontSize: '12px', color: '#4A5568' }}>Scan this to advance the order at a station.</p>
            <button type="button" onClick={() => setShowQrModal(false)} style={{ marginTop: '8px', height: '36px', padding: '0 20px', borderRadius: '999px', border: 'none', background: '#0F2E2A', color: '#F3EFE6', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      ) : null}
      <button
        type="button"
        aria-label="Close order detail panel"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          left: '240px',
          background: 'rgba(0,0,0,0.3)',
          border: 'none',
          zIndex: 40,
        }}
      />

      <aside
        className="order-detail-panel"
        role="dialog"
        aria-label="Order detail"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '400px',
          background: colors.bgCard,
          borderLeft: `1px solid ${colors.border}`,
          zIndex: 50,
          overflowY: 'auto',
          transition: 'transform 0.22s ease',
          transform: 'translateX(0)',
        }}
      >
        <h1 className="sr-only">Order Details</h1>
        <div
          style={{
            position: 'sticky',
            top: 0,
            background: colors.bgCard,
            borderBottom: `1px solid ${colors.border}`,
            padding: '20px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            zIndex: 2,
          }}
        >
          <div>
            <h2
              className="font-display"
              style={{
                margin: '0 0 2px',
                fontSize: '18px',
                fontWeight: 600,
                color: colors.textPrimary,
              }}
            >
              Order Details
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <p
                style={{
                  margin: 0,
                  fontFamily: 'Courier New, monospace',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: colors.textMuted,
                }}
              >
                {orderDetail.order_number}
              </p>
              <button
                type="button"
                aria-label="Print labels"
                onClick={handlePrintLabels}
                style={{
                  border: `1px solid ${colors.border}`,
                  background: 'transparent',
                  borderRadius: '8px',
                  padding: '4px',
                  cursor: 'pointer',
                  display: 'grid',
                  placeItems: 'center',
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = colors.surface
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = 'transparent'
                }}
              >
                <Printer size={14} color={colors.textPrimary} />
              </button>
              <button
                type="button"
                aria-label="Print order label"
                title="Print order QR label (pickup bag)"
                onClick={handlePrintOrderLabel}
                style={{
                  border: `1px solid ${colors.border}`,
                  background: 'transparent',
                  borderRadius: '8px',
                  padding: '4px',
                  cursor: 'pointer',
                  display: 'grid',
                  placeItems: 'center',
                }}
                onMouseEnter={(event) => { event.currentTarget.style.background = colors.surface }}
                onMouseLeave={(event) => { event.currentTarget.style.background = 'transparent' }}
              >
                <Tag size={14} color={colors.textPrimary} />
              </button>
              <button
                type="button"
                aria-label="Open invoice"
                onClick={handleOpenInvoice}
                style={{
                  border: `1px solid ${colors.border}`,
                  background: 'transparent',
                  borderRadius: '8px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: '12px',
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = colors.surface
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = 'transparent'
                }}
              >
                📄
              </button>
              {/* Admins can edit an order at ANY stage (owner override). */}
              {orderDetail.status ? (
                <button
                  type="button"
                  className="font-body"
                  onClick={() => setShowEditModal(true)}
                  style={{
                    height: '32px',
                    padding: '0 14px',
                    borderRadius: '999px',
                    border: `1px solid ${colors.accent}`,
                    background: 'transparent',
                    color: colors.accent,
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Edit Order
                </button>
              ) : null}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <button
              type="button"
              onClick={() => setShowQrModal(true)}
              title="Tap to enlarge for scanning"
              aria-label="Enlarge order QR for scanning"
              style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'grid', placeItems: 'center' }}
            >
              <OrderQRCode
                qrPayload={orderDetail.qr_payload}
                qrToken={orderDetail.qr_token}
                orderId={orderDetail.id}
                orderNumber={orderDetail.order_number}
                size={72}
              />
              <span style={{ fontSize: '9px', color: colors.textMuted, marginTop: '2px' }}>tap to scan</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: 0,
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.opacity = '0.7'
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.opacity = '1'
              }}
            >
              <X size={20} color={colors.textPrimary} />
            </button>
          </div>
        </div>

        <div
          style={{
            padding: '24px 24px 0',
            marginBottom: '24px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span
            className="font-body"
            style={{
              ...statusStyle,
              display: 'inline-block',
              padding: '6px 14px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            {toTitle(orderDetail.status)}
          </span>
          {/* What the customer currently sees on their tracking timeline. */}
          {!isCancelled ? (
            <span
              className="font-body"
              title="The step shown to the customer in their app right now"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 500,
                background: 'rgba(59,130,246,0.10)',
                color: '#3B82F6',
                border: '1px solid rgba(59,130,246,0.25)',
              }}
            >
              Customer sees: {customerFacingLabel(orderDetail.status)}
            </span>
          ) : null}
          {orderDetail.payment_status ? (
            <span
              className="font-body"
              style={{
                padding: '4px 10px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 500,
                background: paymentBadgeBackground,
                color: paymentBadgeColor,
                border: `1px solid ${paymentBadgeBorder}`,
              }}
            >
              {paymentBadgeLabel}
            </span>
          ) : null}
        </div>

        <div
          style={{
            margin: '0 24px 20px',
            padding: '12px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            background: colors.surface,
            display: 'grid',
            gap: '8px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center' }}>
            <div style={{ display: 'grid', gap: '3px' }}>
              <p className="font-body" style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: colors.textPrimary }}>
                Invoice WhatsApp
              </p>
              <p className="font-body" style={{ margin: 0, fontSize: '11px', color: colors.textMuted }}>
                {whatsappInvoice?.sent_at
                  ? `Sent ${formatTimestamp(whatsappInvoice.sent_at)}`
                  : whatsappInvoice?.failed_at
                    ? `Failed ${formatTimestamp(whatsappInvoice.failed_at)}`
                    : whatsappInvoice?.updated_at
                      ? `Updated ${formatTimestamp(whatsappInvoice.updated_at)}`
                      : 'Will send after invoice is ready'}
              </p>
            </div>
            <span
              className="font-body"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '5px 9px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 700,
                color: ['sent', 'delivered', 'read'].includes(whatsappInvoiceStatus)
                  ? 'var(--color-success)'
                  : whatsappInvoiceStatus === 'failed'
                    ? 'var(--color-error)'
                    : colors.textMuted,
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
              }}
            >
              {['sent', 'delivered', 'read'].includes(whatsappInvoiceStatus) ? <Send size={12} /> : null}
              {whatsappInvoiceStatusLabel}
            </span>
          </div>
          {whatsappInvoiceReason ? (
            <p className="font-body" style={{ margin: 0, fontSize: '11px', color: colors.textMuted, wordBreak: 'break-word' }}>
              Reason: {whatsappInvoiceReason}
            </p>
          ) : null}
          {whatsappInvoiceCanRetry ? (
            <button
              type="button"
              className="font-body"
              onClick={() => { void handleResendInvoiceWhatsApp() }}
              disabled={isResendingInvoiceWhatsApp}
              style={{
                justifySelf: 'start',
                height: '30px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '0 10px',
                borderRadius: '8px',
                border: `1px solid ${colors.accent}`,
                background: colors.bgCard,
                color: colors.accent,
                fontSize: '12px',
                fontWeight: 700,
                cursor: isResendingInvoiceWhatsApp ? 'not-allowed' : 'pointer',
                opacity: isResendingInvoiceWhatsApp ? 0.65 : 1,
              }}
            >
              <RefreshCw size={13} />
              {isResendingInvoiceWhatsApp ? 'Retrying...' : 'Retry WhatsApp'}
            </button>
          ) : null}
        </div>

        {orderDetail.status === 'cancelled' && orderDetail.cancellation_reason ? (
          <div style={{ padding: '0 24px', marginBottom: '12px' }}>
            <div
              style={{
                marginTop: '12px',
                padding: '12px',
                background: 'var(--color-error-bg)',
                border: '1px solid var(--color-error-border)',
                borderRadius: '10px',
              }}
            >
              <p
                className="font-body"
                style={{
                  margin: 0,
                  fontSize: '13px',
                  color: 'var(--color-error)',
                }}
              >
                <strong>Cancellation reason:</strong> {orderDetail.cancellation_reason}
              </p>
            </div>
          </div>
        ) : null}

        <ReschedulePendingCard
          order={orderDetail}
          onOrderUpdated={(updated) => setOrderDetail(updated)}
        />

        {actionError ? (
          <div style={{ padding: '0 24px 16px' }}>
            <div
              className="font-body"
              style={{
                background: 'var(--color-error-bg)',
                color: 'var(--color-error)',
                border: '1px solid var(--color-error-border)',
                borderRadius: '10px',
                padding: '10px 12px',
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              {actionError}
            </div>
          </div>
        ) : null}

        <section style={{ marginBottom: '24px', padding: '0 24px' }}>
          <p
            className="font-body"
            style={{
              margin: '0 0 12px',
              fontSize: '11px',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Agent Live Position
          </p>
          <div
            className="font-body"
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '10px',
              padding: '10px 12px',
              background: colors.surface,
              fontSize: '13px',
              color: colors.textSecondary,
            }}
          >
            {distanceFromPickupKm != null && latestAgentPosition
              ? `Agent is ${distanceFromPickupKm.toFixed(1)} km from pickup, last update ${formatLastSeen(latestAgentPosition.recordedAt)}`
              : 'Waiting for agent location update'}
          </div>
        </section>

        {(orderDetail.special_instructions || orderDetail.bag_count != null || (orderDetail.inspection_tags && orderDetail.inspection_tags.length > 0) || orderDetail.inspection_notes) ? (
          <section
            style={{
              background: colors.bgSecondary,
              borderRadius: '12px',
              padding: '16px',
              margin: '0 24px 24px',
            }}
          >
            {orderDetail.special_instructions ? (
              <div style={{ marginBottom: '12px' }}>
                <p className="font-body" style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>
                  Special Instructions
                </p>
                <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textSecondary, lineHeight: 1.5 }}>
                  {orderDetail.special_instructions}
                </p>
              </div>
            ) : null}
            {(orderDetail.inspection_notes || orderDetail.bag_count != null || (orderDetail.inspection_tags && orderDetail.inspection_tags.length > 0)) ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: colors.textPrimary }}>
                    Pickup inspection
                  </p>
                  <p className="font-body" style={{ margin: 0, fontSize: '11px', color: colors.textMuted }}>
                    Uploaded {formatTimestamp(orderDetail.updated_at)}
                  </p>
                </div>
                {orderDetail.bag_count ? (
                  <span
                    className="font-body"
                    style={{
                      display: 'inline-flex',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      background: 'rgba(214,185,123,0.16)',
                      color: colors.textPrimary,
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  >
                    {orderDetail.bag_count} bags
                  </span>
                ) : null}
                {orderDetail.inspection_tags && orderDetail.inspection_tags.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                    {orderDetail.inspection_tags.map((tag) => (
                      <span
                        key={tag}
                        className="font-body"
                        style={{
                          ...inspectionTagStyle(String(tag).toLowerCase()),
                          padding: '4px 10px',
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontWeight: 700,
                        }}
                      >
                        {toTitle(tag)}
                      </span>
                    ))}
                  </div>
                ) : null}
                {orderDetail.inspection_notes ? (
                  <blockquote
                    className="font-body"
                    style={{
                      margin: '12px 0 0',
                      borderLeft: `3px solid ${colors.accent}`,
                      padding: '8px 10px',
                      borderRadius: '8px',
                      background: colors.surface,
                      color: colors.textSecondary,
                      fontSize: '13px',
                    }}
                  >
                    {orderDetail.inspection_notes}
                  </blockquote>
                ) : null}
              </div>
            ) : null}
          </section>
        ) : null}

        <section style={{ marginBottom: '24px', padding: '0 24px' }}>
          <p
            className="font-body"
            style={{
              margin: '0 0 12px',
              fontSize: '11px',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Status Pipeline
          </p>

          <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto', paddingBottom: '6px' }}>
            {STATUS_STEPS.map((step, index) => {
              const isCompleted = !isCancelled && index < safeIndex
              const isCurrent = !isCancelled && index === safeIndex
              const stepTime = getStepTime(step.key, index)
              const circleBorder = isCompleted || isCurrent ? colors.accent : colors.border
              const circleBackground = isCompleted ? colors.accent : isCurrent ? colors.bgCard : colors.surface

              return (
                <div key={step.key} style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '78px' }}>
                    <div
                      style={{
                        width: '26px',
                        height: '26px',
                        borderRadius: '999px',
                        border: `2px solid ${circleBorder}`,
                        background: circleBackground,
                        display: 'grid',
                        placeItems: 'center',
                        position: 'relative',
                        boxShadow: isCurrent ? '0 0 0 4px rgba(214,185,123,0.15)' : 'none',
                      }}
                    >
                      {isCompleted ? <Check size={12} color={colors.bgCard} /> : null}
                      {isCurrent ? (
                        <span
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '999px',
                            background: '#D6B97B',
                            display: 'inline-block',
                          }}
                        />
                      ) : null}
                      {isCancelled ? (
                        <span
                          style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'grid',
                            placeItems: 'center',
                            color: '#DC2626',
                            fontSize: '12px',
                            fontWeight: 700,
                          }}
                        >
                          ✕
                        </span>
                      ) : null}
                    </div>
                    <p className="font-body" style={{ margin: '8px 0 2px', fontSize: '11px', fontWeight: 600, color: colors.textPrimary, textAlign: 'center' }}>
                      {step.label}
                    </p>
                    {stepTime ? (
                      <p className="font-body" style={{ margin: 0, fontSize: '10px', color: colors.textMuted, textAlign: 'center' }}>
                        {stepTime}
                      </p>
                    ) : null}
                  </div>

                  {index < STATUS_STEPS.length - 1 ? (
                    <div
                      style={{
                        width: '42px',
                        height: '2px',
                        background: isCompleted ? '#D6B97B' : colors.border,
                        margin: '0 6px',
                      }}
                    />
                  ) : null}
                </div>
              )
            })}
          </div>

          {needsPickupAgent ? (
            <button
              type="button"
              className="font-body"
              onClick={() => setShowAgentPicker((prev) => !prev)}
              style={{
                marginTop: '16px',
                width: '100%',
                minHeight: '48px',
                borderRadius: '999px',
                border: 'none',
                background: '#D6B97B',
                color: colors.textPrimary,
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
              disabled={isUpdatingStatus}
            >
              {showAgentPicker ? 'Close Agent Picker' : 'Assign Agent for Pickup'}
            </button>
          ) : null}
          {needsPickupAgent && showAgentPicker ? (
            <AgentPickerDropdown
              orderId={orderDetail.id}
              currentAgentId={agentId || null}
              currentAgentName={assignedAgentName}
              onAssigned={() => {
                setShowAgentPicker(false)
                void refreshOrder()
              }}
              onClose={() => setShowAgentPicker(false)}
            />
          ) : null}

          {needsDeliveryAgent ? (
            <button
              type="button"
              className="font-body"
              onClick={() => setShowAgentPicker((prev) => !prev)}
              style={{
                marginTop: '16px',
                width: '100%',
                minHeight: '48px',
                borderRadius: '999px',
                border: 'none',
                background: '#D6B97B',
                color: colors.textPrimary,
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
              disabled={isUpdatingStatus}
            >
              {showAgentPicker ? 'Close Agent Picker' : 'Assign Agent for Delivery'}
            </button>
          ) : null}
          {needsDeliveryAgent && showAgentPicker ? (
            <AgentPickerDropdown
              orderId={orderDetail.id}
              currentAgentId={agentId || null}
              currentAgentName={assignedAgentName}
              onAssigned={() => {
                setShowAgentPicker(false)
                void refreshOrder()
              }}
              onClose={() => setShowAgentPicker(false)}
            />
          ) : null}

          {canAdvanceStatus ? (
            <button
              type="button"
              className="font-body"
              onClick={() => {
                void handleAdvanceStatus()
              }}
              style={{
                marginTop: '16px',
                width: '100%',
                minHeight: '48px',
                borderRadius: '999px',
                border: 'none',
                background: '#D6B97B',
                color: colors.textPrimary,
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
              disabled={isUpdatingStatus}
            >
              {isUpdatingStatus ? 'Updating status...' : `Move to: ${toTitle(nextStep.key)}`}
            </button>
          ) : null}

          <FinalBillCard order={orderDetail} onRefresh={refreshOrder} />
          <OrderProofsSection order={orderDetail} deliveries={orderDetail.deliveries} />

          {canCancelLifecycle ? (
            <button
              type="button"
              className="font-body"
              onClick={() => setShowCancelDialog(true)}
              style={{
                marginTop: '10px',
                width: '100%',
                minHeight: '44px',
                borderRadius: '10px',
                border: '1.5px solid #FCA5A5',
                background: 'transparent',
                color: '#DC2626',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
              disabled={isUpdatingStatus}
            >
              Cancel
            </button>
          ) : null}
          {canMarkReturned ? (
            <button
              type="button"
              className="font-body"
              onClick={() => setShowReturnedDialog(true)}
              style={{
                marginTop: '10px',
                width: '100%',
                minHeight: '44px',
                borderRadius: '10px',
                border: `1.5px solid ${colors.border}`,
                background: 'transparent',
                color: colors.textPrimary,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Mark returned
            </button>
          ) : null}
          {canMarkDelivered ? (
            <button
              type="button"
              className="font-body"
              onClick={() => { void handleMarkDelivered() }}
              disabled={markingDelivered}
              style={{
                marginTop: '10px',
                width: '100%',
                minHeight: '44px',
                borderRadius: '10px',
                border: '1.5px solid #86EFAC',
                background: 'transparent',
                color: '#15803D',
                fontSize: '13px',
                fontWeight: 600,
                cursor: markingDelivered ? 'default' : 'pointer',
                opacity: markingDelivered ? 0.6 : 1,
              }}
            >
              {markingDelivered ? 'Marking…' : 'Mark Delivered'}
            </button>
          ) : null}
          {canForceFail ? (
            <button
              type="button"
              className="font-body"
              onClick={() => setShowForceFailDialog(true)}
              style={{
                marginTop: '10px',
                width: '100%',
                minHeight: '44px',
                borderRadius: '10px',
                border: '1.5px solid #FCA5A5',
                background: 'transparent',
                color: '#DC2626',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Force fail
            </button>
          ) : null}
        </section>

        <section style={{ marginBottom: '24px', padding: '0 24px' }}>
          <p
            className="font-body"
            style={{
              margin: '0 0 12px',
              fontSize: '11px',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Customer
          </p>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
            <div
              className="font-body"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '999px',
                background: '#D6B97B',
                color: colors.textPrimary,
                display: 'grid',
                placeItems: 'center',
                fontSize: '12px',
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {getInitials(orderDetail.customer?.name || orderDetail.customer_name)}
            </div>

            <div>
              <p
                className="font-body"
                style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: colors.textPrimary }}
              >
                {orderDetail.customer?.name || orderDetail.customer_name || 'Customer'}
              </p>
              <p
                className="font-body"
                style={{ margin: '3px 0 0', fontSize: '13px', fontWeight: 400, color: colors.textMuted }}
              >
                {orderDetail.customer?.phone || orderDetail.customer_phone || '-'}
              </p>
            </div>
          </div>

          <p
            className="font-body"
            style={{ margin: 0, fontSize: '14px', fontWeight: 400, color: colors.textSecondary, lineHeight: 1.5 }}
          >
            {orderDetail.address?.full_address || 'Address not available'}
            {orderDetail.address?.landmark ? `, ${orderDetail.address.landmark}` : ''}
          </p>

          {orderDetail.address?.lat && orderDetail.address?.lng && (
            <AddressMiniMap
              lat={Number(orderDetail.address.lat)}
              lng={Number(orderDetail.address.lng)}
            />
          )}

          {(() => {
            const mapsUrl = buildMapsUrl({
              lat: orderDetail.address?.lat,
              lng: orderDetail.address?.lng,
              address: orderDetail.address?.full_address,
            })
            return mapsUrl ? (
              <div style={{ marginTop: '12px' }}>
                <OpenInMapsLink url={mapsUrl} />
              </div>
            ) : null
          })()}
        </section>

        <OrderGroupsSection orderId={orderDetail.id} displayId={orderDetail.display_id} />
        <OrderExceptionsSection orderId={orderDetail.id} />

        <WhatsAppPanel
          orderId={orderDetail.id}
          customerName={orderDetail.customer?.name || orderDetail.customer_name || 'Customer'}
          customerPhone={orderDetail.customer?.phone || orderDetail.customer_phone || ''}
          orderNumber={orderDetail.order_number}
          orderStatus={orderDetail.status}
          agentName={orderDetail.agent_name || undefined}
          agentId={agentId || undefined}
          paymentMode={orderDetail.payment_mode || undefined}
        />

        <section
          style={{
            background: colors.bgSecondary,
            borderRadius: '12px',
            padding: '16px',
            margin: '0 24px 24px',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <p
                className="font-body"
                style={{
                  margin: '0 0 6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                }}
              >
                {(orderDetail.group_count ?? 0) >= 2 ? 'Service Groups' : 'Service'}
              </p>
              <p className="font-body" style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                {(orderDetail.group_count ?? 0) >= 2
                  ? `${orderDetail.group_count} groups · ${orderDetail.items_ready ?? 0}/${orderDetail.items_total ?? 0} ready`
                  : formatServiceName(orderDetail.service_name || items[0]?.service_name || items[0]?.name)}
              </p>
              <p className="font-body" style={{ margin: '4px 0 0', fontSize: '12px', fontWeight: 400, color: colors.textMuted }}>
                {orderDetail.delivery_promise_label || getServicePromise(orderDetail.service_name || items[0]?.service_name || items[0]?.name, orderDetail.delivery_model)}
              </p>
              {orderDetail.delivery_model === 'instant' ? (
                /* The 4-hour tier. Distinct from Express (~40 min) so the floor
                   can tell at a glance which promise this order is running on. */
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                  <span
                    className="font-body"
                    style={{
                      borderRadius: '999px',
                      background: '#ECFDF5',
                      color: '#047857',
                      border: '1px solid #6EE7B7',
                      padding: '3px 8px',
                      fontSize: '10px',
                      fontWeight: 700,
                    }}
                  >
                    Instant 4h
                  </span>
                </div>
              ) : null}
              {orderDetail.delivery_model === 'express' ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                  <span
                    className="font-body"
                    style={{
                      borderRadius: '999px',
                      background: '#FEF2F2',
                      color: '#B91C1C',
                      border: '1px solid #FCA5A5',
                      padding: '3px 8px',
                      fontSize: '10px',
                      fontWeight: 700,
                    }}
                  >
                    Express
                  </span>
                  {orderDetail.workload_minutes != null || orderDetail.workload_score != null ? (
                    <span className="font-body" style={{ fontSize: '11px', color: colors.textMuted }}>
                      {Number(orderDetail.workload_minutes ?? orderDetail.workload_score)} min workload
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div>
              <p
                className="font-body"
                style={{
                  margin: '0 0 6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                }}
              >
                Pickup Slot
              </p>
              <p className="font-body" style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                {orderDetail.pickup_slot || '-'}
              </p>
              {orderDetail.delivery_model === 'express' && orderDetail.express_slot_key ? (
                <p className="font-body" style={{ margin: '4px 0 0', fontSize: '12px', color: colors.textMuted }}>
                  Slot: {orderDetail.express_slot_key}
                </p>
              ) : null}
              {orderDetail.promised_delivery_at ? (
                <p className="font-body" style={{ margin: '4px 0 0', fontSize: '12px', color: colors.textMuted }}>
                  Promise: {formatTimestamp(orderDetail.promised_delivery_at)}
                </p>
              ) : null}
            </div>
          </div>

          <div style={{ margin: '12px 0', height: '1px', background: colors.border }} />

          <div>
            <p
              className="font-body"
              style={{
                margin: '0 0 6px',
                fontSize: '11px',
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: 'uppercase',
              }}
            >
              Partner
            </p>
            <p className="font-body" style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
              {orderDetail.partner?.name || orderDetail.partner_name || '-'}
            </p>
          </div>
        </section>

        <section style={{ marginBottom: '24px', padding: '0 24px' }}>
          <p
            className="font-body"
            style={{
              margin: '0 0 12px',
              fontSize: '11px',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Items {isLoadingDetail ? '' : `(${items.length})`}
          </p>

          {isLoadingDetail ? (
            <div aria-live="polite" style={{ display: 'grid', gap: '6px', marginBottom: '10px' }}>
              <span className="sr-only">Loading items</span>
              {[0, 1, 2].map((i) => (
                <div
                  key={`item-sk-${i}`}
                  style={{ height: '40px', borderRadius: '8px', background: colors.bgSecondary }}
                />
              ))}
            </div>
          ) : null}

          {(orderDetail.group_count ?? 0) >= 2 ? (
            <p className="font-body" style={{ margin: '0 0 8px', fontSize: '13px', color: colors.textMuted }}>
              Items are organised by service group above.
            </p>
          ) : null}
          {(orderDetail.group_count ?? 0) >= 2 ? null : items.map((item) => (
            <div
              key={item.id}
              style={{
                background: colors.bgSecondary,
                borderRadius: '8px',
                padding: '10px 12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <div>
                <p className="font-body" style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: colors.textPrimary }}>
                  {formatServiceName(item.service_name || item.name || 'Item')}
                </p>
                <p className="font-body" style={{ margin: '3px 0 0', fontSize: '13px', fontWeight: 400, color: colors.textMuted }}>
                  {formatItemQty(item.quantity, item.unit, item.item_count)}
                </p>
              </div>
              <p className="font-body" style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                {formatPrice(item.total_price || 0)}
              </p>
            </div>
          ))}

          {(() => {
            // Amount breakdown - one source of truth from the backend order record
            // (same values the invoice + customer timeline use). `total` is already
            // the post-coupon amount the customer pays. We show the math so an admin
            // immediately sees items total, the minimum-bill charge, the coupon
            // discount and the final payable.
            const itemsTotal = Number(
              orderDetail.subtotal ?? items.reduce((sum, it) => sum + Number(it.total_price || 0), 0),
            )
            const deliveryFee = Number(orderDetail.delivery_fee || 0)
            const discount = Number(orderDetail.discount || 0)
            const finalPayable = getCalculatedPayable(orderDetail)
            const finalAmountPaid = orderDetail.final_amount_paid != null ? Number(orderDetail.final_amount_paid) : null
            const manualAdjustment = orderDetail.manual_adjustment_amount != null
              ? Number(orderDetail.manual_adjustment_amount)
              : finalAmountPaid != null ? roundMoney(finalAmountPaid - finalPayable) : 0
            const rowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' } as const
            const labelStyle = { margin: 0, fontSize: '13px', color: colors.textMuted } as const
            const valueStyle = { margin: 0, fontSize: '13px', fontWeight: 500, color: colors.textPrimary } as const
            return (
              <div
                style={{
                  borderTop: `1px solid ${colors.border}`,
                  marginTop: '10px',
                  paddingTop: '12px',
                }}
              >
                <div style={rowStyle}>
                  <p className="font-body" style={labelStyle}>Items total</p>
                  <p className="font-body" style={valueStyle}>{formatPrice(itemsTotal)}</p>
                </div>
                {deliveryFee > 0 ? (
                  <div style={rowStyle}>
                    <p className="font-body" style={labelStyle}>Additional charge (min. bill ₹149)</p>
                    <p className="font-body" style={valueStyle}>{formatPrice(deliveryFee)}</p>
                  </div>
                ) : null}
                {discount > 0 ? (
                  <div style={rowStyle}>
                    <p className="font-body" style={labelStyle}>
                      Discount{orderDetail.coupon_code ? ` (${orderDetail.coupon_code})` : ''}
                    </p>
                    <p className="font-body" style={{ ...valueStyle, color: 'var(--color-success, #2e7d32)' }}>
                      − {formatPrice(discount)}
                    </p>
                  </div>
                ) : null}
                <div
                  style={{
                    borderTop: `1px solid ${colors.border}`,
                    marginTop: '6px',
                    paddingTop: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <p className="font-body" style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: colors.textPrimary }}>
                    {finalAmountPaid != null ? 'Final Amount Paid' : discount > 0 ? 'Customer paid' : 'Total Amount'}
                  </p>
                  <p className="font-display" style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: colors.textPrimary }}>
                    {formatPrice(finalAmountPaid ?? finalPayable)}
                  </p>
                </div>
                {finalAmountPaid != null && manualAdjustment !== 0 ? (
                  <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p className="font-body" style={labelStyle}>Manual adjustment</p>
                    <p className="font-body" style={{ ...valueStyle, color: manualAdjustment < 0 ? 'var(--color-success, #2e7d32)' : colors.accent }}>
                      {manualAdjustment > 0 ? '+' : '−'} {formatPrice(Math.abs(manualAdjustment))}
                    </p>
                  </div>
                ) : null}
                {finalAmountPaid != null && orderDetail.manual_adjustment_remark ? (
                  <p className="font-body" style={{ margin: '6px 0 0', fontSize: '12px', color: colors.textMuted }}>
                    Remark: {orderDetail.manual_adjustment_remark}
                  </p>
                ) : null}
                <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p className="font-body" style={labelStyle}>
                    {(orderDetail.payment_mode || '').toLowerCase() === 'cod' ? 'Cash on Delivery' : 'Online payment'}
                  </p>
                  <p className="font-body" style={{ ...valueStyle, textTransform: 'capitalize' }}>
                    {orderDetail.payment_status || 'pending'}
                  </p>
                </div>
                <div
                  style={{
                    marginTop: '14px',
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`,
                    background: colors.bgSecondary,
                    display: 'grid',
                    gap: '10px',
                  }}
                >
                  <p
                    className="font-body"
                    style={{
                      margin: 0,
                      fontSize: '11px',
                      fontWeight: 700,
                      color: colors.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Payment Summary
                  </p>
                  {/* Status, method and amount stated outright. "Paid" on its own
                      forced the admin to guess how the money arrived, which is
                      exactly the fact needed to reconcile a day's cash. */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                    <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textMuted }}>
                      Status
                    </p>
                    <p className="font-body" style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: paymentBadgeColor }}>
                      {paymentStatusValue === 'pending' ? 'Payment Pending' : paymentBadgeLabel}
                    </p>
                  </div>
                  {paymentStatusValue === 'paid' ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                        <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textMuted }}>
                          Method
                        </p>
                        <p
                          className="font-body"
                          style={{
                            margin: 0,
                            fontSize: '13px',
                            fontWeight: 700,
                            color: collectedMethodRaw ? colors.textPrimary : colors.textMuted,
                          }}
                        >
                          {collectedMethodLabel}
                        </p>
                      </div>
                      {orderDetail.paid_at ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                          <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textMuted }}>
                            Received
                          </p>
                          <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textPrimary }}>
                            {formatTimestamp(orderDetail.paid_at)}
                          </p>
                        </div>
                      ) : null}
                    </>
                  ) : null}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                    <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textMuted }}>
                      Calculated Payable Amount
                    </p>
                    <p className="font-body" style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: colors.textPrimary }}>
                      {formatPrice(finalPayable)}
                    </p>
                  </div>
                  <label className="font-body" style={{ display: 'grid', gap: '5px', fontSize: '12px', color: colors.textMuted }}>
                    Final Amount Paid
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: colors.textMuted, fontSize: '13px' }}>₹</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={finalAmountPaidDraft}
                        onChange={(event) => setFinalAmountPaidDraft(event.target.value)}
                        disabled={!canUpdateOrders || savingPaymentSummary || updatingPayment}
                        className="font-body"
                        style={{
                          width: '100%',
                          height: '38px',
                          padding: '0 10px 0 26px',
                          borderRadius: '8px',
                          border: `1px solid ${finalAmountPaidIsValid ? colors.border : 'var(--color-error)'}`,
                          background: colors.bgCard,
                          color: colors.textPrimary,
                          fontSize: '14px',
                          fontWeight: 700,
                          opacity: !canUpdateOrders ? 0.75 : 1,
                        }}
                      />
                    </div>
                  </label>
                  <label className="font-body" style={{ display: 'grid', gap: '5px', fontSize: '12px', color: colors.textMuted }}>
                    Remark (optional)
                    <textarea
                      value={adjustmentRemarkDraft}
                      onChange={(event) => setAdjustmentRemarkDraft(event.target.value)}
                      disabled={!canUpdateOrders || savingPaymentSummary || updatingPayment}
                      placeholder="Reason for adjustment"
                      className="font-body"
                      rows={2}
                      style={{
                        width: '100%',
                        minHeight: '54px',
                        padding: '9px 10px',
                        borderRadius: '8px',
                        border: `1px solid ${colors.border}`,
                        background: colors.bgCard,
                        color: colors.textPrimary,
                        fontSize: '13px',
                        resize: 'vertical',
                        opacity: !canUpdateOrders ? 0.75 : 1,
                      }}
                    />
                  </label>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>
                      Difference:{' '}
                      <strong style={{ color: manualAdjustmentPreview < 0 ? 'var(--color-success)' : manualAdjustmentPreview > 0 ? colors.accent : colors.textPrimary }}>
                        {manualAdjustmentPreview === 0 ? formatPrice(0) : `${manualAdjustmentPreview > 0 ? '+' : '−'}${formatPrice(Math.abs(manualAdjustmentPreview))}`}
                      </strong>
                    </p>
                    {canUpdateOrders ? (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className="font-body"
                          onClick={() => {
                            void handleSavePaymentSummary()
                          }}
                          disabled={savingPaymentSummary || updatingPayment || !finalAmountPaidIsValid}
                          style={{
                            height: '36px',
                            padding: '0 14px',
                            borderRadius: '999px',
                            border: `1px solid ${colors.accent}`,
                            background: colors.bgCard,
                            color: colors.accent,
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: savingPaymentSummary || updatingPayment || !finalAmountPaidIsValid ? 'not-allowed' : 'pointer',
                            opacity: savingPaymentSummary || updatingPayment || !finalAmountPaidIsValid ? 0.65 : 1,
                          }}
                        >
                          {savingPaymentSummary ? 'Saving...' : 'Save/Update Payment'}
                        </button>
                        {/* Mark Paid is offered whenever money is still owed, not
                            only after delivery - cash and UPI are routinely taken
                            at pickup. Settled and non-billable orders get no
                            button at all. */}
                        {canMarkPaid ? (
                          markPaidMenuOpen ? (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>
                                Received via
                              </span>
                              {(['cash', 'upi'] as const).map((method) => (
                                <button
                                  key={method}
                                  type="button"
                                  className="font-body"
                                  onClick={() => {
                                    void handleMarkPaid(method)
                                  }}
                                  disabled={markPaidDisabled}
                                  style={{
                                    height: '36px',
                                    padding: '0 16px',
                                    borderRadius: '999px',
                                    border: 'none',
                                    background: 'var(--color-success)',
                                    color: '#FFFFFF',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    cursor: markPaidDisabled ? 'not-allowed' : 'pointer',
                                    opacity: markPaidDisabled ? 0.65 : 1,
                                  }}
                                >
                                  {method === 'upi' ? 'UPI' : 'Cash'}
                                </button>
                              ))}
                              <button
                                type="button"
                                className="font-body"
                                onClick={() => setMarkPaidMenuOpen(false)}
                                disabled={updatingPayment}
                                style={{
                                  height: '36px',
                                  padding: '0 12px',
                                  borderRadius: '999px',
                                  border: `1px solid ${colors.border}`,
                                  background: colors.bgCard,
                                  color: colors.textMuted,
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: updatingPayment ? 'not-allowed' : 'pointer',
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="font-body"
                              onClick={() => setMarkPaidMenuOpen(true)}
                              disabled={markPaidDisabled}
                              style={{
                                height: '36px',
                                padding: '0 14px',
                                borderRadius: '999px',
                                border: 'none',
                                background: 'var(--color-success)',
                                color: '#FFFFFF',
                                fontSize: '12px',
                                fontWeight: 700,
                                cursor: markPaidDisabled ? 'not-allowed' : 'pointer',
                                opacity: markPaidDisabled ? 0.65 : 1,
                              }}
                            >
                              {updatingPayment ? 'Updating...' : 'Mark Payment Received'}
                            </button>
                          )
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  {orderDetail.manual_adjustment_updated_at ? (
                    <p className="font-body" style={{ margin: 0, fontSize: '11px', color: colors.textMuted }}>
                      Last updated {formatTimestamp(orderDetail.manual_adjustment_updated_at)}
                    </p>
                  ) : null}
                </div>
                {(() => {
                  // Processed weight - by-weight orders only. Shown whenever the
                  // order has a per_kg line so ops have somewhere to record what
                  // the scale said; per-item orders are untouched.
                  //
                  // Booked weight = the per_kg line(s) original quantity (what the
                  // customer ordered at). Processed = orders.weight_kg, entered at
                  // the hub. An order that was never washed (cancelled/returned/
                  // refunded) has no processed weight, so no entry field.
                  const weightLines = items.filter(
                    (it) => String(it.unit || '').toLowerCase() === 'per_kg'
                  )
                  const isWeightOrder = weightLines.length > 0
                  if (!isWeightOrder && orderDetail.weight_kg == null) return null
                  const estWeight = weightLines.reduce((s, it) => s + Number(it.quantity || 0), 0)
                  const processedWeight =
                    orderDetail.weight_kg != null ? Number(orderDetail.weight_kg) : null
                  const wasProcessed = !['cancelled', 'returned', 'refunded'].includes(
                    String(orderDetail.status)
                  )
                  const estAmount = Number(orderDetail.estimated_amount ?? orderDetail.total ?? 0)
                  const finalAmount = Number(orderDetail.final_amount ?? orderDetail.total ?? 0)
                  const trim = (n: number) => String(Math.round(n * 100) / 100)
                  // The amount row belongs to the final-bill flow. Recording a
                  // weight does not reprice, so showing "estimated → final" then
                  // would imply a recalculation that never happened.
                  const showAmountRow =
                    !!orderDetail.final_bill_status &&
                    orderDetail.final_bill_status !== 'not_required'
                  const badge =
                    orderDetail.final_bill_status === 'approved'
                      ? { text: 'Weight Verified', bg: '#dcfce7', fg: '#15803d' }
                      : orderDetail.final_bill_status === 'pending_customer'
                        ? { text: 'Awaiting customer approval', bg: '#fef9c3', fg: '#854d0e' }
                        : orderDetail.final_bill_status === 'rejected'
                          ? { text: 'Bill rejected', bg: '#fee2e2', fg: '#b91c1c' }
                          : null
                  return (
                    <div style={{ borderTop: `1px solid ${colors.border}`, marginTop: '10px', paddingTop: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <p className="font-body" style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                          Processed weight
                        </p>
                        {badge ? (
                          <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px', background: badge.bg, color: badge.fg }}>
                            {badge.text}
                          </span>
                        ) : null}
                      </div>
                      {estWeight > 0 ? (
                        <div style={rowStyle}>
                          <p className="font-body" style={labelStyle}>Booked weight</p>
                          <p className="font-body" style={valueStyle}>{trim(estWeight)} kg</p>
                        </div>
                      ) : null}
                      <div style={rowStyle}>
                        <p className="font-body" style={labelStyle}>Actual weight washed</p>
                        <p className="font-body" style={valueStyle}>
                          {processedWeight != null ? (
                            <strong>{trim(processedWeight)} kg</strong>
                          ) : (
                            <span style={{ color: colors.textMuted }}>Not recorded</span>
                          )}
                        </p>
                      </div>
                      {showAmountRow ? (
                        <div style={rowStyle}>
                          <p className="font-body" style={labelStyle}>Amount (estimated → final)</p>
                          <p className="font-body" style={valueStyle}>{formatPrice(estAmount)} → <strong>{formatPrice(finalAmount)}</strong></p>
                        </div>
                      ) : null}
                      {orderDetail.reweigh_note ? (
                        <div style={{ ...rowStyle, alignItems: 'flex-start' }}>
                          <p className="font-body" style={labelStyle}>Reason</p>
                          <p className="font-body" style={{ ...valueStyle, maxWidth: '60%', textAlign: 'right' }}>{orderDetail.reweigh_note}</p>
                        </div>
                      ) : null}
                      {isWeightOrder && wasProcessed ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                          <label htmlFor="processed-weight-input" className="font-body" style={{ ...labelStyle, margin: 0 }}>
                            Record weight (kg)
                          </label>
                          <input
                            id="processed-weight-input"
                            type="number"
                            step="0.01"
                            min="0"
                            value={weightDraft}
                            onChange={(event) => setWeightDraft(event.target.value)}
                            placeholder="e.g. 5.8"
                            className="font-body"
                            style={{
                              width: '90px',
                              padding: '6px 8px',
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              fontSize: '12px',
                            }}
                          />
                          <button
                            type="button"
                            onClick={handleSaveWeight}
                            disabled={isSavingWeight}
                            className="font-body"
                            style={{
                              border: 'none',
                              background: colors.textPrimary,
                              color: colors.surface,
                              borderRadius: '6px',
                              padding: '7px 12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: isSavingWeight ? 'default' : 'pointer',
                              opacity: isSavingWeight ? 0.7 : 1,
                            }}
                          >
                            {isSavingWeight ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )
                })()}
              </div>
            )
          })()}
        </section>

        <section style={{ marginBottom: '24px', padding: '0 24px' }}>
          {assignedAgentName ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span
                  aria-label={assignedAgentOnline ? 'Agent online' : 'Agent offline'}
                  title={assignedAgentOnline ? 'Online' : 'Offline'}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '999px',
                    background: assignedAgentOnline ? '#15803D' : '#9CA3AF',
                    flexShrink: 0,
                  }}
                />
                <div
                  className="font-body"
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '999px',
                    background: colors.textPrimary,
                    color: colors.surface,
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  {getInitials(assignedAgentName)}
                </div>
                <div style={{ flex: 1 }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                    {assignedAgentName}
                  </p>
                  <p className="font-body" style={{ margin: '2px 0 0', fontSize: '12px', fontWeight: 400, color: colors.textMuted }}>
                    Pickup Agent · {assignedAgentOnline ? 'Online' : 'Offline'}
                  </p>
                </div>
                <button
                  type="button"
                  className="font-body"
                  onClick={() => setShowAgentPicker((prev) => !prev)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '999px',
                    border: `1px solid ${colors.accent}`,
                    background: 'transparent',
                    color: colors.accent,
                    fontSize: '11px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {showAgentPicker ? 'Close' : 'Reassign'}
                </button>
              </div>
              {showAgentPicker ? (
                <AgentPickerDropdown
                  orderId={orderDetail.id}
                  currentAgentId={agentId || null}
                  currentAgentName={assignedAgentName}
                  onAssigned={() => {
                    setShowAgentPicker(false)
                    void refreshOrder()
                  }}
                  onClose={() => setShowAgentPicker(false)}
                />
              ) : null}
            </div>
          ) : !needsPickupAgent && !needsDeliveryAgent ? (
            <div>
              <button
                type="button"
                className="font-body"
                onClick={() => setShowAgentPicker((prev) => !prev)}
                style={{
                  width: '100%',
                  border: `1.5px solid ${colors.accent}`,
                  background: 'rgba(214,185,123,0.12)',
                  color: colors.accent,
                  borderRadius: '10px',
                  padding: '11px 14px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {showAgentPicker ? 'Close Agent Picker' : 'Assign Agent'}
              </button>
              {showAgentPicker ? (
                <AgentPickerDropdown
                  orderId={orderDetail.id}
                  currentAgentId={agentId || null}
                  currentAgentName={assignedAgentName}
                  onAssigned={() => {
                    setShowAgentPicker(false)
                    void refreshOrder()
                  }}
                  onClose={() => setShowAgentPicker(false)}
                />
              ) : null}
            </div>
          ) : (
            <span
              className="font-body"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                border: '1px solid #D6B97B',
                borderRadius: '999px',
                background: 'rgba(214,185,123,0.12)',
                color: '#D6B97B',
                padding: '5px 12px',
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              Unassigned
            </span>
          )}
        </section>

        <section style={{ marginBottom: '24px', padding: '0 24px' }}>
          <div style={{ marginTop: '16px' }}>
            <p
              className="font-body"
              style={{
                margin: '0 0 8px',
                fontSize: '11px',
                fontWeight: 700,
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Laundry Partner
            </p>
            {orderDetail.partner_name ? (
              <p
                className="font-body"
                style={{
                  margin: '0 0 8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.textPrimary,
                }}
              >
                {orderDetail.partner_name}
              </p>
            ) : null}
            <select
              value={orderDetail.partner_id || ''}
              onChange={async (e) => {
                const partnerId = e.target.value
                if (!partnerId) {
                  return
                }
                setAssigningPartner(true)
                try {
                  await adminApi.assignPartner(orderDetail.id, partnerId)
                  await refreshOrder()
                  const label = partners.find((p) => p.id === partnerId)?.name
                  if (label) {
                    setOrderDetail((prev) => ({
                      ...prev,
                      partner_id: partnerId,
                      partner_name: label,
                    }))
                  }
                  addToast('Partner assigned successfully', 'success')
                } catch (err: unknown) {
                  const message =
                    err &&
                    typeof err === 'object' &&
                    'response' in err &&
                    err.response &&
                    typeof err.response === 'object' &&
                    'data' in err.response &&
                    err.response.data &&
                    typeof err.response.data === 'object' &&
                    'message' in err.response.data
                      ? String((err.response.data as { message?: string }).message)
                      : 'Failed to assign partner'
                  addToast(message, 'error')
                } finally {
                  setAssigningPartner(false)
                }
              }}
              disabled={assigningPartner}
              // Fetch the partner list only when the admin reaches for it.
              onFocus={loadPartners}
              onMouseEnter={loadPartners}
              className="font-body"
              style={{
                width: '100%',
                height: '40px',
                borderRadius: '10px',
                border: `1.5px solid ${colors.border}`,
                padding: '0 12px',
                fontSize: '13px',
                color: colors.textPrimary,
                background: colors.surface,
                cursor: assigningPartner ? 'not-allowed' : 'pointer',
              }}
            >
              <option value="">
                {assigningPartner ? 'Assigning...' : partners.length ? 'Select partner...' : 'Select partner…'}
              </option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section style={{ marginBottom: '24px', padding: '0 24px' }}>
          <p
            className="font-body"
            style={{
              margin: '0 0 12px',
              fontSize: '11px',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Order Timeline
          </p>

          {timelineEvents.map((event, index) => {
            const isCompleted = event.state === 'completed'
            const isCurrent = event.state === 'current'
            const lineColor = isCompleted ? '#86EFAC' : colors.border

            return (
              <div key={`${event.label}-${index}`} style={{ display: 'flex', gap: '10px' }}>
                <div style={{ width: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '999px',
                      border: isCompleted
                        ? '1px solid #86EFAC'
                        : isCurrent
                          ? '1px solid #D6B97B'
                          : `1px solid ${colors.border}`,
                      background: isCompleted ? '#15803D' : isCurrent ? '#D6B97B' : colors.surface,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    {isCompleted ? (
                      <Check size={12} color={colors.bgCard} />
                    ) : isCurrent ? (
                      <span
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '999px',
                          background: colors.textPrimary,
                          display: 'inline-block',
                        }}
                      />
                    ) : null}
                  </div>

                  {index < timelineEvents.length - 1 ? (
                    <span
                      style={{
                        width: '2px',
                        height: '16px',
                        background: lineColor,
                        display: 'inline-block',
                      }}
                    />
                  ) : null}
                </div>

                <div style={{ paddingTop: '2px', paddingBottom: '10px' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                    {event.label}
                  </p>
                  <p className="font-body" style={{ margin: '2px 0 0', fontSize: '12px', fontWeight: 400, color: colors.textMuted }}>
                    {event.time ? new Date(event.time).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'Pending'}
                  </p>
                </div>
              </div>
            )
          })}
        </section>
        <section style={{ marginBottom: '24px', padding: '0 24px' }}>
          <button
            onClick={() => setIsActivityExpanded(!isActivityExpanded)}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              padding: '0',
              cursor: 'pointer',
            }}
          >
            <p
              className="font-body"
              style={{
                margin: '0',
                fontSize: '11px',
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Activity Log {auditEvents.length > 0 ? `(${auditEvents.length})` : ''}
            </p>
            <span style={{ fontSize: '12px', color: colors.textMuted }}>
              {isActivityExpanded ? '▼' : '▶'}
            </span>
          </button>

          {isActivityExpanded && (
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {isLoadingAudit ? (
                <p className="font-body" style={{ fontSize: '13px', color: colors.textMuted }}>Loading activity...</p>
              ) : auditEvents.length === 0 ? (
                <p className="font-body" style={{ fontSize: '13px', color: colors.textMuted }}>No activity recorded yet.</p>
              ) : (
                auditEvents.map((event, idx) => (
                  (() => {
                    const eventKey = String(event.id || idx)
                    const actorKind = event.actor?.kind || event.actor_kind || 'system'
                    const actorId = event.actor?.id || event.actor_id
                    const happenedAt = event.occurred_at || event.created_at
                    const isDetailsOpen = Boolean(expandedActivityDetails[eventKey])
                    return (
                  <div key={event.id || idx} style={{
                    background: colors.bgSecondary,
                    padding: '10px 12px',
                    borderRadius: '8px',
                  }}>
                    <p className="font-body" style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                      {event.event || event.event_name}
                    </p>
                    {event.geofence_warn ? (
                      <p className="font-body" style={{ margin: '0 0 6px', fontSize: '12px', color: '#B45309' }}>
                        Geofence warning recorded for this transition
                      </p>
                    ) : null}
                    {(event.from_state || event.to_state || event.has_proof) ? (
                      <p className="font-body" style={{ margin: '0 0 6px', fontSize: '12px', color: colors.textMuted }}>
                        {event.from_state && event.to_state ? `${event.from_state} → ${event.to_state}` : event.to_state || event.from_state}
                        {event.has_proof ? ` · proof${event.proof_kind ? `:${event.proof_kind}` : ''}` : ''}
                      </p>
                    ) : null}
                    {(event.actor_name || event.station || event.scan_location) ? (
                      <p className="font-body" style={{ margin: '0 0 6px', fontSize: '12px', color: colors.textMuted }}>
                        {event.station ? `📷 ${event.station}` : '📷 scan'}
                        {event.actor_name ? ` · by ${event.actor_name}` : ''}
                        {event.scan_location ? ` · @ ${event.scan_location.lat.toFixed(4)}, ${event.scan_location.lng.toFixed(4)}` : ''}
                      </p>
                    ) : null}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className="font-body" style={{ fontSize: '12px', color: colors.textSecondary }}>
                        {actorKind} {actorId ? `(${actorId.substring(0,8)})` : ''}
                      </span>
                      <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>
                        {formatTimestamp(happenedAt)}
                      </span>
                    </div>
                    {(event.payload_hash || event.request_id) ? (
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedActivityDetails((prev) => ({ ...prev, [eventKey]: !prev[eventKey] }))
                        }}
                        style={{
                          marginTop: '8px',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: colors.textMuted,
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        {isDetailsOpen ? '▼ Hide details' : '▶ Show details'}
                      </button>
                    ) : null}
                    {isDetailsOpen ? (
                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${colors.border}` }}>
                        {event.request_id ? (
                          <p className="font-body" style={{ margin: '0 0 4px', fontSize: '12px', color: colors.textMuted }}>
                            request_id: {event.request_id}
                          </p>
                        ) : null}
                        {event.payload_hash ? (
                          <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted, wordBreak: 'break-all' }}>
                            payload_hash: {event.payload_hash}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                    )
                  })()
                ))
              )}
            </div>
          )}
        </section>
        <section style={{ marginBottom: '24px', padding: '0 24px' }}>
          <p
            className="font-body"
            style={{
              margin: '0 0 12px',
              fontSize: '11px',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Admin Notes
          </p>

          {orderDetail.admin_notes ? (
            <div
              className="font-body"
              style={{
                background: colors.bgSecondary,
                border: `1.5px solid ${colors.border}`,
                borderRadius: '12px',
                padding: '12px',
                fontSize: '13px',
                color: colors.textPrimary,
                marginBottom: '12px',
                lineHeight: 1.5,
              }}
            >
              {orderDetail.admin_notes}
            </div>
          ) : null}

          <textarea
            value={notesDraft}
            onChange={(event) => setNotesDraft(event.target.value)}
            placeholder="Add admin notes for this order"
            className="font-body"
            style={{
              width: '100%',
              minHeight: '110px',
              borderRadius: '10px',
              border: `1.5px solid ${colors.border}`,
              padding: '10px 12px',
              fontSize: '13px',
              color: colors.textPrimary,
              resize: 'vertical',
            }}
          />

          <button
            type="button"
            className="font-body"
            onClick={() => {
              void handleSaveNotes()
            }}
            style={{
              marginTop: '12px',
              width: '100%',
              minHeight: '44px',
              borderRadius: '10px',
              border: `1.5px solid ${colors.accent}`,
              background: 'rgba(214,185,123,0.12)',
              color: colors.textPrimary,
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
            disabled={isSavingNotes}
          >
            {isSavingNotes ? 'Saving notes...' : 'Save Notes'}
          </button>
        </section>

        <div style={{ padding: '0 24px 24px', display: 'flex', gap: '12px' }}>
          <button
            type="button"
            className="font-body"
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              borderRadius: '10px',
              background: colors.textPrimary,
              color: colors.surface,
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Update Status
          </button>
          <button
            type="button"
            className="font-body"
            style={{
              flex: 1,
              padding: '12px',
              border: `1.5px solid ${colors.border}`,
              borderRadius: '10px',
              background: 'transparent',
              color: colors.textPrimary,
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Print
          </button>
        </div>

        <div style={{ padding: '0 24px 24px' }}>
          <button
            type="button"
            className="font-body"
            onClick={() => setShowReorderModal(true)}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '999px',
              border: `1.5px solid ${colors.textPrimary}`,
              background: colors.bgCard,
              color: colors.textPrimary,
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            🔄 Reorder
          </button>
        </div>

        {showCancelDialog && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100,
              background: 'rgba(0,0,0,0.5)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <div
              style={{
                background: colors.bgCard,
                borderRadius: '16px',
                padding: '24px',
                width: '400px',
                maxWidth: '90vw',
                border: `1px solid ${colors.border}`,
              }}
            >
              <h3
                className="font-display"
                style={{
                  margin: '0 0 12px',
                  fontSize: '18px',
                  fontWeight: 700,
                  color: 'var(--color-error)',
                }}
              >
                Cancel Order #{orderDetail.order_number}
              </h3>
              <p
                className="font-body"
                style={{
                  margin: '0 0 16px',
                  fontSize: '13px',
                  color: colors.textSecondary,
                }}
              >
                Please provide a reason for cancellation. This will be recorded in the order history.
              </p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Reason for cancellation..."
                className="font-body"
                style={{
                  width: '100%',
                  minHeight: '80px',
                  border: `1.5px solid ${colors.border}`,
                  borderRadius: '10px',
                  padding: '10px 12px',
                  fontSize: '14px',
                  color: colors.textPrimary,
                  background: colors.surface,
                  marginBottom: '16px',
                }}
              />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="font-body"
                  onClick={() => {
                    setShowCancelDialog(false)
                    setCancelReason('')
                  }}
                  style={{
                    height: '40px',
                    padding: '0 16px',
                    borderRadius: '999px',
                    border: `1px solid ${colors.border}`,
                    background: 'transparent',
                    color: colors.textPrimary,
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Go Back
                </button>
                <button
                  type="button"
                  className="font-body"
                  onClick={() => {
                    void handleConfirmCancel()
                  }}
                  disabled={cancelling}
                  style={{
                    height: '40px',
                    padding: '0 16px',
                    borderRadius: '999px',
                    border: 'none',
                    background: 'var(--color-error)',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: cancelling ? 'not-allowed' : 'pointer',
                  }}
                >
                  {cancelling ? 'Cancelling...' : 'Confirm Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}
        {showReturnedDialog ? (
          <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.5)', display: 'grid', placeItems: 'center' }}>
            <div style={{ background: colors.bgCard, borderRadius: '16px', padding: '24px', width: '400px', maxWidth: '90vw', border: `1px solid ${colors.border}` }}>
              <h3 className="font-display" style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>Mark returned</h3>
              <textarea
                value={returnedReason}
                onChange={(e) => setReturnedReason(e.target.value)}
                placeholder="Reason (at least 10 characters)..."
                className="font-body"
                style={{ width: '100%', minHeight: '80px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '10px 12px', fontSize: '14px', color: colors.textPrimary, background: colors.surface, marginBottom: '16px' }}
              />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="font-body" onClick={() => { setShowReturnedDialog(false); setReturnedReason('') }} style={{ height: '40px', padding: '0 16px', borderRadius: '999px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textPrimary, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Go Back</button>
                <button type="button" className="font-body" disabled={markingReturned || returnedReason.trim().length < 10} onClick={() => { void handleMarkReturned() }} style={{ height: '40px', padding: '0 16px', borderRadius: '999px', border: 'none', background: colors.textPrimary, color: '#FFFFFF', fontSize: '13px', fontWeight: 600, cursor: 'pointer', opacity: returnedReason.trim().length < 10 ? 0.6 : 1 }}>
                  {markingReturned ? 'Saving...' : 'Confirm return'}
                </button>
              </div>
            </div>
          </div>
        ) : null}


        {showForceFailDialog ? (
          <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.5)', display: 'grid', placeItems: 'center' }}>
            <div style={{ background: colors.bgCard, borderRadius: '16px', padding: '24px', width: '400px', maxWidth: '90vw', border: `1px solid ${colors.border}` }}>
              <h3 className="font-display" style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: 700, color: 'var(--color-error)' }}>Force fail order</h3>
              {(() => {
                const isCod = String(orderDetail.payment_mode || '').toLowerCase() === 'cod'
                const isPaid = String(orderDetail.payment_status || '').toLowerCase() === 'paid'
                const refundRequired = !isCod && isPaid
                const rows: Array<[string, string]> = [
                  ['Order', orderDetail.display_id || orderDetail.order_number],
                  ['Customer', orderDetail.customer_name || orderDetail.customer?.name || '-'],
                  ['Current status', customerFacingLabel(orderDetail.status)],
                  ['Payment', `${isCod ? 'COD' : 'Online'} · ${orderDetail.payment_status || 'pending'}`],
                  ['Amount', formatPrice(Number(orderDetail.final_amount_paid ?? getCalculatedPayable(orderDetail)))],
                ]
                return (
                  <div style={{ background: colors.bgSecondary, borderRadius: '10px', padding: '12px', marginBottom: '14px' }}>
                    {rows.map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>{k}</span>
                        <span className="font-body" style={{ fontSize: '12px', fontWeight: 600, color: colors.textPrimary, textTransform: k === 'Payment' ? 'capitalize' : 'none' }}>{v}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', paddingTop: '8px', borderTop: `1px solid ${colors.border}` }}>
                      <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>Refund required?</span>
                      <span className="font-body" style={{ fontSize: '12px', fontWeight: 700, color: refundRequired ? 'var(--color-error)' : colors.textPrimary }}>
                        {refundRequired ? `Yes, ${formatPrice(Number(orderDetail.final_amount_paid ?? getCalculatedPayable(orderDetail)))} will be refunded` : 'No (nothing captured)'}
                      </span>
                    </div>
                  </div>
                )
              })()}
              <textarea
                value={forceFailReason}
                onChange={(e) => setForceFailReason(e.target.value)}
                placeholder="Reason (at least 10 characters)..."
                className="font-body"
                style={{ width: '100%', minHeight: '80px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '10px 12px', fontSize: '14px', color: colors.textPrimary, background: colors.surface, marginBottom: '16px' }}
              />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="font-body" onClick={() => { setShowForceFailDialog(false); setForceFailReason('') }} style={{ height: '40px', padding: '0 16px', borderRadius: '999px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textPrimary, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Go Back</button>
                <button type="button" className="font-body" disabled={forceFailing || forceFailReason.trim().length < 10} onClick={() => { void handleForceFail() }} style={{ height: '40px', padding: '0 16px', borderRadius: '999px', border: 'none', background: 'var(--color-error)', color: '#FFFFFF', fontSize: '13px', fontWeight: 600, cursor: 'pointer', opacity: forceFailReason.trim().length < 10 ? 0.6 : 1 }}>
                  {forceFailing ? 'Saving...' : 'Confirm force fail'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        <ReorderModal
          isOpen={showReorderModal}
          orderId={orderDetail.id}
          onClose={() => setShowReorderModal(false)}
          onSuccess={() => {
            if (onReorderSuccess) {
              onReorderSuccess()
            }
          }}
        />
      </aside>

      {showEditModal ? (
        <CreateOrderModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onCreated={() => {
            setShowEditModal(false)
            window.location.reload()
          }}
          editContext={{
            orderId: orderDetail.id,
            orderNumber: orderDetail.order_number || orderDetail.display_id || '',
            customer: {
              id: orderDetail.customer?.id || '',
              name: orderDetail.customer?.name,
              phone: orderDetail.customer?.phone,
              email: orderDetail.customer?.email,
            },
            address: orderDetail.address?.id
              ? { id: orderDetail.address.id, full_address: orderDetail.address.full_address, label: orderDetail.address.label }
              : null,
            items: (orderDetail.items || [])
              .filter((it) => it.service_item_id)
              .map((it) => ({
                service_item_id: it.service_item_id as string,
                quantity: Number(it.quantity || 0),
                item_count: it.item_count == null ? null : Number(it.item_count),
              })),
            pickupDate: orderDetail.pickup_date || undefined,
            pickupSlot: orderDetail.pickup_slot || undefined,
            deliveryDate: orderDetail.delivery_date || undefined,
            deliverySlot: orderDetail.delivery_slot || undefined,
            paymentMode: String(orderDetail.payment_mode || '').toLowerCase() === 'online' ? 'online' : 'cod',
            adminNotes: orderDetail.admin_notes || undefined,
            specialInstructions: orderDetail.special_instructions || undefined,
            couponCode: orderDetail.coupon_code || undefined,
          }}
        />
      ) : null}
    </>
  )
}
